import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getBackendBaseUrl } from "../../../utils/apiUrl";
import { generateSmartOcrExtraction } from "../../../utils/smartOcrParser";
import {
  FaCar, FaCheckCircle, FaQrcode,
  FaChevronRight, FaTimes, FaUndo, FaTag, FaUpload,
  FaCoins, FaPercentage, FaExchangeAlt, FaShieldAlt, FaCheck, FaIdBadge,
  FaPaperPlane, FaRobot, FaQuestionCircle, FaExclamationTriangle, FaShieldVirus,
  FaSlidersH, FaBuilding, FaCarSide, FaSparkles, FaInfoCircle, FaStar, FaAward,
  FaArrowRight, FaTrophy, FaFileAlt, FaLock, FaChevronDown, FaChevronUp,
  FaMobileAlt, FaSyncAlt, FaExternalLinkAlt, FaSearchPlus, FaUserCheck, FaEye,
  FaIdCard, FaFileInvoiceDollar, FaEdit, FaBars, FaPrint
} from "react-icons/fa";
import DealPrintReport from "./DealPrintReport";

// MASTER DYNAMIC DATASET FALLBACK
const MASTER_DATASET = {
  dealership: {
    name: "ABC Automotive",
    business_manager: "Bobby Bonser",
    registration_state: "Colorado",
    tax_rate: 0.07,
    doc_fee: 800,
    registration_fee: 772
  },
  lenders: [
    { lender_id: "L001", _id: "L001", name: "Toyota Financial Services", type: "Captive (OEM)", isActive: true },
    { lender_id: "L002", _id: "L002", name: "Capital One Auto Finance", type: "Major Bank", isActive: true },
    { lender_id: "L003", _id: "L003", name: "Wells Fargo Auto", type: "Major Bank", isActive: true },
    { lender_id: "L004", _id: "L004", name: "Bank of America Auto", type: "Major Bank", isActive: true }
  ],
  lender_plans: [
    {
      plan_id: "LP001",
      _id: "LP001",
      lender_id: "L001",
      lenderName: "Toyota Financial Services",
      name: "New Vehicle Standard 60",
      apr: 5.90,
      term_months: 60,
      term: 60,
      credit_requirements: "Tier 1 Prime",
      vehicle_rules: "New Toyota Only",
      aiRecommendation: "⭐ Lowest APR — Saves max total interest with OEM captive rate.",
      isActive: true
    },
    {
      plan_id: "LP002",
      _id: "LP002",
      lender_id: "L002",
      lenderName: "Capital One Auto Finance",
      name: "Prime Auto Financing 60",
      apr: 6.25,
      term_months: 60,
      term: 60,
      credit_requirements: "Tier 1 & 2 Prime",
      vehicle_rules: "New & Used Eligible",
      aiRecommendation: "Fast digital underwriting with flexible loan-to-value allowance.",
      isActive: true
    },
    {
      plan_id: "LP003",
      _id: "LP003",
      lender_id: "L003",
      lenderName: "Wells Fargo Auto",
      name: "Standard Used Auto 72",
      apr: 6.45,
      term_months: 72,
      term: 72,
      credit_requirements: "Prime & Near Prime",
      vehicle_rules: "Used Certified Eligible",
      aiRecommendation: "Great extended term for certified pre-owned vehicles.",
      isActive: true
    },
    {
      plan_id: "LP004",
      _id: "LP004",
      lender_id: "L004",
      lenderName: "Bank of America Auto",
      name: "Dealer Partner 72",
      apr: 6.90,
      term_months: 72,
      term: 72,
      credit_requirements: "Tier 1 - 3 Prime",
      vehicle_rules: "New & Used Eligible",
      aiRecommendation: "⚖️ Best Equity Balance — Balanced monthly cash flow & rapid principal paydown.",
      isActive: true
    },
    {
      plan_id: "LP005",
      _id: "LP005",
      lender_id: "L004",
      lenderName: "Bank of America Auto",
      name: "Extended Horizon 84",
      apr: 7.45,
      term_months: 84,
      term: 84,
      credit_requirements: "Tier 1 Only",
      vehicle_rules: "New Only",
      aiRecommendation: "💰 Lowest Monthly Payment — Maximizes budget comfort with lowest payment.",
      isActive: true
    }
  ],
  fi_products: [
    {
      product_id: "FI001",
      _id: "FI001",
      name: "Vehicle Service Contract (VSC)",
      short_name: "VSC",
      category: "Mechanical Protection",
      price: 3100,
      taxable: false,
      isTaxable: false,
      term: "7 yr / 100,000 mi",
      description: "Comprehensive bumper-to-bumper and powertrain mechanical failure protection, 24/7 roadside assistance, and rental reimbursement.",
      benefits: [
        "100% parts & labor coverage nationwide",
        "Engine, transmission, drive axle & electrical protection",
        "24/7 roadside assistance & towing",
        "Rental car reimbursement during repairs"
      ],
      warningMsg: "By declining VSC, you forfeit 100% parts & labor coverage for mechanical & electrical failures after factory warranty expires, risking thousands in unexpected repair bills.",
      provider: "Toyota Financial Services",
      isActive: true
    },
    {
      product_id: "FI002",
      _id: "FI002",
      name: "Prepaid Maintenance Upgrade",
      short_name: "Maintenance",
      category: "Factory Scheduled Care",
      price: 1200,
      taxable: false,
      isTaxable: false,
      term: "4 yr / 60,000 mi",
      description: "Factory scheduled oil, filter, tire rotations, brake fluid checks, multipoint inspections, and fluid top-offs.",
      benefits: [
        "All factory-scheduled maintenance services included",
        "Synthetic oil, filters, tire rotations & brake inspections",
        "Locked-in current labor rates against inflation",
        "Valid at all authorized dealership service centers"
      ],
      warningMsg: "By declining Prepaid Maintenance, you will pay full out-of-pocket retail prices for oil changes, tire rotations, and scheduled services at every dealership visit.",
      provider: "Toyota Financial Services",
      isActive: true
    },
    {
      product_id: "FI003",
      _id: "FI003",
      name: "Platinum Plus Appearance & Wheel",
      short_name: "Platinum Plus",
      category: "Cosmetic & Road Hazard",
      price: 1295,
      taxable: true,
      isTaxable: true,
      term: "3 yr / Unlimited",
      description: "Alloy wheel curb rash, road hazard tire replacements, paintless dent repair, and front windshield chip protection.",
      benefits: [
        "Alloy wheel curb scuff and scrape refinishing",
        "Tire replacement & puncture repair from road hazards",
        "Paintless dent removal for door dings",
        "Windshield crack and chip repair"
      ],
      warningMsg: "By declining Platinum Plus, you will be liable for costly cosmetic damage, alloy wheel curb repairs, road hazard tire blowouts, and windshield cracks.",
      provider: "Toyota Financial Services",
      isActive: true
    },
    {
      product_id: "FI004",
      _id: "FI004",
      name: "Guaranteed Asset Protection (GAP)",
      short_name: "GAP",
      category: "Total Loss Coverage",
      price: 995,
      taxable: false,
      isTaxable: false,
      term: "72 months",
      description: "Waives the difference between vehicle actual cash value and remaining loan balance in the event of total loss or theft.",
      benefits: [
        "Waives up to 125% MSRP/Loan balance deficiency",
        "Covers your primary auto insurance deductible up to $1,000",
        "Prevents having to pay thousands for a car you no longer have",
        "Protects your credit rating during a total loss claim"
      ],
      warningMsg: "By declining GAP, you will be personally responsible for paying the entire remaining loan balance out-of-pocket if your vehicle is totaled or stolen.",
      provider: "Toyota Financial Services",
      isActive: true
    }
  ]
};

// Helper to convert YouTube URL into full bleed background video embed
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const str = String(url).trim();
  const regExp = /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/ ]{11})/i;
  const match = str.match(regExp);
  if (match && match[1] && match[1].length === 11) {
    const videoId = match[1];
    const origin = typeof window !== 'undefined' ? encodeURIComponent(window.location.origin) : '';
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&showinfo=0&rel=0&iv_load_policy=3&modestbranding=1&playsinline=1&enablejsapi=1&origin=${origin}`;
  }
  return null;
};

// Typewriter animated question component
const TypewriterQuestion = ({ text, onComplete, subtitle, tagText = "DEAL STUDIO", speed = 16 }) => {
  const [displayedText, setDisplayedText] = useState("");
  const [isDone, setIsDone] = useState(false);

  useEffect(() => {
    setDisplayedText("");
    setIsDone(false);
    let index = 0;
    if (!text) return;

    const interval = setInterval(() => {
      index++;
      setDisplayedText(text.substring(0, index));
      if (index >= text.length) {
        clearInterval(interval);
        setIsDone(true);
        if (onComplete) onComplete();
      }
    }, speed);

    return () => clearInterval(interval);
  }, [text, speed]);

  const handleSkip = () => {
    setDisplayedText(text);
    setIsDone(true);
    if (onComplete) onComplete();
  };

  return (
    <div className="space-y-2 cursor-pointer select-none" onClick={handleSkip}>
      <div className="flex items-center gap-2">
        <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping inline-block" />
        <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">{tagText}</span>
        {!isDone && <span className="text-[9px] text-slate-400 font-bold">(click to skip)</span>}
      </div>
      <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight leading-snug min-h-[58px]">
        {displayedText}
        {!isDone && <span className="inline-block w-2.5 h-6 bg-cyan-400 ml-1.5 animate-pulse align-middle" />}
      </h2>
      {subtitle && isDone && (
        <p className="text-xs text-slate-300 font-medium animate-fadeInUp">{subtitle}</p>
      )}
    </div>
  );
};

// Modern Pill-Shaped Input Box with Gradient Round Arrow Button (Matching Image 3)
const RoundedPillInput = ({
  value,
  onChange,
  onSubmit,
  placeholder = "Type your response...",
  autoFocus = true,
  disabled = false
}) => {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (onSubmit && (typeof value === 'string' ? value.trim() : value)) {
          onSubmit();
        }
      }}
      className="relative flex items-center w-full bg-[#0c1324] border border-slate-700/80 hover:border-cyan-500/50 focus-within:border-cyan-400 focus-within:ring-2 focus-within:ring-cyan-400/20 rounded-full py-2 pl-6 pr-2 shadow-[inset_0_2px_8px_rgba(0,0,0,0.6)] transition-all"
    >
      <input
        type="text"
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className="w-full bg-transparent text-white text-sm outline-none placeholder-slate-400 font-medium pr-3"
      />
      <button
        type="submit"
        disabled={disabled || !(typeof value === 'string' ? value.trim() : value)}
        className="w-11 h-11 rounded-full bg-gradient-to-tr from-cyan-400 via-teal-300 to-orange-200 text-slate-950 flex items-center justify-center flex-shrink-0 shadow-lg hover:scale-105 active:scale-95 transition-transform cursor-pointer disabled:opacity-40 disabled:scale-100 disabled:cursor-not-allowed border-0"
      >
        <FaArrowRight size={14} className="text-slate-950 font-black" />
      </button>
    </form>
  );
};

const KioskView = () => {
  const { token: routeToken } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [token, setToken] = useState(routeToken);
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  // 1. Dynamic Dealership State
  const [dealershipData, setDealershipData] = useState(MASTER_DATASET.dealership);

  // 2. Dynamic Catalog & Vehicles State
  const [showroomCars, setShowroomCars] = useState([]);
  const [featuredCar, setFeaturedCar] = useState(null);
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedTrim, setSelectedTrim] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showBrowseInventory, setShowBrowseInventory] = useState(false);
  const [leftViewMode, setLeftViewMode] = useState("builder"); // "builder" | "dealStudio"
  const [sidebarVideoError, setSidebarVideoError] = useState(false);

  // 3. Dynamic Customer State (Step 1)
  const [customerName, setCustomerName] = useState("");

  // 4. Questionnaire States (12 Steps Total)
  const [activeQuestionStep, setActiveQuestionStep] = useState(1); // 1 to 12
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [paymentPreference, setPaymentPreference] = useState(null); // null until Step 9 (FINANCE | CASH)
  const [creditTier, setCreditTier] = useState("Tier 1 · 721–780");

  // 5. Down Payment & Rebate State (Defaults to 0 so deal board is 100% real-time)
  const [customDownPayment, setCustomDownPayment] = useState(0);
  const [activeRebate, setActiveRebate] = useState(0); // Starts at $0 until coupon code entered
  const [appliedCouponCode, setAppliedCouponCode] = useState("");

  // 6. Lenders & Plans State (Step 12)
  const [lendersList, setLendersList] = useState(MASTER_DATASET.lenders);
  const [lenderPlansList, setLenderPlansList] = useState(MASTER_DATASET.lender_plans);
  const [selectedLenderId, setSelectedLenderId] = useState("L001");
  const [selectedPlanIds, setSelectedPlanIds] = useState(["LP001"]);
  const [selectedTerm, setSelectedTerm] = useState(60);
  const [annualMiles, setAnnualMiles] = useState("15k / year");

  // 7. Trade-In State (Steps 3-7)
  const [hasTrade, setHasTrade] = useState(null); // null | true | false
  const [tradeVehicleInput, setTradeVehicleInput] = useState("");
  const [tradeDetails, setTradeDetails] = useState({
    make: "Honda",
    model: "Civic",
    year: "2019",
    color: "Silver",
    mileageRange: "30,000 – 60,000 mi",
    exactMileage: "",
    condition: "Good",
    conditionText: "Normal wear, all services done",
    payoff: 0,
    exactPayoffInput: ""
  });
  const [customTradeMileageInput, setCustomTradeMileageInput] = useState("");
  const [customTradeConditionInput, setCustomTradeConditionInput] = useState("");
  const [customTradePayoffInput, setCustomTradePayoffInput] = useState("");

  const [aiValuationLoading, setAiValuationLoading] = useState(false);
  const [aiValuationResult, setAiValuationResult] = useState(null);
  const [tradeAllowanceValuation, setTradeAllowanceValuation] = useState(0);
  const [tradePayoff, setTradePayoff] = useState(0);

  // 8. F&I Protection Products (By default ALL plans selected, collapsed)
  const [fandiList, setFandiList] = useState(MASTER_DATASET.fi_products);
  const [selectedFAndIIds, setSelectedFAndIIds] = useState(MASTER_DATASET.fi_products.map(p => p.product_id || p._id)); // Default: ALL selected
  const [previouslyUnselectedIds, setPreviouslyUnselectedIds] = useState([]); // Track unselected items to trigger reselect script
  const [aimeeFiScript, setAimeeFiScript] = useState(""); // Inline AI F&I Manager dynamic message
  const [expandedFandiSlot, setExpandedFandiSlot] = useState(null); // Default: null (all collapsed / not opened)
  const [showAllLoanPlans, setShowAllLoanPlans] = useState(false); // Step 11: Toggle for seeing other loan options
  const [uploadedDocs, setUploadedDocs] = useState({});
  const [pendingBasePlanDecline, setPendingBasePlanDecline] = useState(null);
  const [viewingFAndIPlan, setViewingFAndIPlan] = useState(null);

  // 9. Salesperson Hand-off state
  const [salespersonId, setSalespersonId] = useState("");
  const [salespersonSubmitted, setSalespersonSubmitted] = useState(false);

  // 10. Draft Reload Warning Modal State
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [draftCarInfo, setDraftCarInfo] = useState(null);

  // 11. Bottom Conversational Textbox & Floating AI Speech Bubble
  const [conversationalInput, setConversationalInput] = useState("");
  const [aiSpeechResponse, setAiSpeechResponse] = useState(null);
  const speechBubbleTimerRef = useRef(null);
  const showroomVideoRef = useRef(null);

  // 12. F&I Connect Live Companion State
  const [showPhoneSimulator, setShowPhoneSimulator] = useState(false);
  const [selectedDocPreview, setSelectedDocPreview] = useState(null);
  const [managerNotesInput, setManagerNotesInput] = useState("");
  const [companionSessionTime, setCompanionSessionTime] = useState(600); // 10 minutes countdown

  // 13. Mobile / Tablet Responsive Drawer & Desk OCR Upload States
  const [mobileDealBoardOpen, setMobileDealBoardOpen] = useState(false);
  const [deskUploadingSlot, setDeskUploadingSlot] = useState(null);
  const [editingOcrSlotDesk, setEditingOcrSlotDesk] = useState(null);
  const [editOcrFieldsDesk, setEditOcrFieldsDesk] = useState({});
  const [showPrintReportModal, setShowPrintReportModal] = useState(false);

  // Auto-detect Logged-in Salesperson or URL Query Params
  useEffect(() => {
    const qSpId = searchParams.get("salespersonId");
    const qSpName = searchParams.get("salespersonName");
    if (qSpId || qSpName) {
      const formatted = qSpName ? (qSpId && !qSpName.includes(qSpId) ? `${qSpName} (${qSpId})` : qSpName) : qSpId;
      setSalespersonId(formatted);
      setSalespersonSubmitted(true);
      return;
    }

    try {
      const loggedUser = JSON.parse(localStorage.getItem("user") || "{}");
      if (loggedUser && (loggedUser.role === "Salesperson" || loggedUser.firstName)) {
        const name = `${loggedUser.firstName || ''} ${loggedUser.lastName || ''}`.trim() || loggedUser.name || "Sales Consultant";
        const spIdFormatted = `${name} (${loggedUser._id ? loggedUser._id.slice(-6).toUpperCase() : 'SP-ACTIVE'})`;
        setSalespersonId(spIdFormatted);
        setSalespersonSubmitted(true);
      }
    } catch (_) {}
  }, [searchParams]);

  // Reset options visibility whenever step changes
  useEffect(() => {
    setOptionsVisible(false);
  }, [activeQuestionStep]);

  // Ensure showroom video autoplays smoothly
  useEffect(() => {
    if (showroomVideoRef.current) {
      showroomVideoRef.current.defaultMuted = true;
      showroomVideoRef.current.muted = true;
      showroomVideoRef.current.play().catch(() => { });
    }
  }, [featuredCar?.videoUrl]);

  // Session timer countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setCompanionSessionTime(prev => (prev > 0 ? prev - 1 : 600));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Sync token from route & check local draft
  useEffect(() => {
    if (routeToken) {
      setToken(routeToken);
      fetchDealDetails(routeToken, true);
    } else {
      setToken(null);
      setDeal(null);
      const savedDraftToken = localStorage.getItem("pohanka_active_deal_token");
      const savedDraftData = localStorage.getItem("pohanka_active_deal_data");
      if (savedDraftToken && savedDraftData) {
        try {
          const parsed = JSON.parse(savedDraftData);
          setDraftCarInfo(parsed);
          setShowDraftModal(true);
        } catch (_) { }
      }
    }
  }, [routeToken]);

  // Live polling to continuously sync customer mobile uploads, signatures, and verification with salesperson desk
  useEffect(() => {
    const activeTok = token || routeToken;
    if (!activeTok) return;
    const interval = setInterval(() => {
      fetchDealDetails(activeTok, false);
    }, 2000);
    return () => clearInterval(interval);
  }, [token, routeToken]);

  // Instant 0ms Cross-Tab Sync (for same browser multi-tab testing & live kiosk stations)
  useEffect(() => {
    const activeTok = token || routeToken;
    if (!activeTok) return;

    // Check if there is already a local broadcast payload
    try {
      const localData = localStorage.getItem(`deal_sync_${activeTok}`);
      if (localData) {
        const parsed = JSON.parse(localData);
        if (parsed.uploadedDocuments && Object.keys(parsed.uploadedDocuments).length > 0) {
          setUploadedDocs(prev => ({ ...prev, ...parsed.uploadedDocuments }));
        }
        if (parsed.signature) {
          setDeal(prev => prev ? { ...prev, signature: parsed.signature } : { signature: parsed.signature });
        }
      }
    } catch (_) {}

    const handleStorageEvent = (e) => {
      if (e.key === `deal_sync_${activeTok}` && e.newValue) {
        try {
          const parsed = JSON.parse(e.newValue);
          if (parsed.uploadedDocuments && Object.keys(parsed.uploadedDocuments).length > 0) {
            setUploadedDocs(prev => ({ ...prev, ...parsed.uploadedDocuments }));
          }
          if (parsed.signature) {
            setDeal(prev => prev ? { ...prev, signature: parsed.signature } : { signature: parsed.signature });
          }
        } catch (_) {}
      }
    };

    window.addEventListener("storage", handleStorageEvent);

    let channel = null;
    if (typeof window.BroadcastChannel !== "undefined") {
      channel = new BroadcastChannel("kiosk_deal_sync");
      channel.onmessage = (event) => {
        if (event.data?.token === activeTok) {
          if (event.data.uploadedDocuments && Object.keys(event.data.uploadedDocuments).length > 0) {
            setUploadedDocs(prev => ({ ...prev, ...event.data.uploadedDocuments }));
          }
          if (event.data.signature) {
            setDeal(prev => prev ? { ...prev, signature: event.data.signature } : { signature: event.data.signature });
          }
        }
      };
    }

    return () => {
      window.removeEventListener("storage", handleStorageEvent);
      if (channel) channel.close();
    };
  }, [token, routeToken]);

  // Fetch dynamic master dataset from backend
  useEffect(() => {
    initializeKiosk();
  }, []);

  const initializeKiosk = async () => {
    setLoading(true);
    try {
      const carRes = await fetch(`${getBackendBaseUrl()}/d2-cars`);
      if (carRes.ok) {
        const carData = await carRes.json();
        if (carData.success && carData.data && carData.data.length > 0) {
          setShowroomCars(carData.data);
          setFeaturedCar(carData.data[0]);
          const firstColor = carData.data[0].colors?.[0]?.name || carData.data[0].color || "";
          setSelectedColor(firstColor);
          setSelectedTrim(carData.data[0].trim || "");
        }
      }

      const [lenderRes, planRes, fandiRes, dsRes] = await Promise.all([
        fetch(`${getBackendBaseUrl()}/d2-lenders`),
        fetch(`${getBackendBaseUrl()}/d2-lenders/plans`),
        fetch(`${getBackendBaseUrl()}/d2-fandi`),
        fetch(`${getBackendBaseUrl()}/d2-deals/kiosk-dataset`)
      ]);

      const [ldData, plData, fiData, dsData] = await Promise.all([
        lenderRes.json().catch(() => ({})),
        planRes.json().catch(() => ({})),
        fandiRes.json().catch(() => ({})),
        dsRes.json().catch(() => ({}))
      ]);

      if (ldData.success && ldData.data?.length > 0) {
        setLendersList(ldData.data);
        setSelectedLenderId(ldData.data[0]._id || ldData.data[0].lenderId || "L001");
      }
      if (plData.success && plData.data?.length > 0) {
        setLenderPlansList(plData.data);
        setSelectedPlanIds([plData.data[0]._id || plData.data[0].planId || "LP001"]);
      }
      if (fiData.success && fiData.data?.length > 0) {
        setFandiList(fiData.data);
        setSelectedFAndIIds(fiData.data.map(p => p.product_id || p._id || p.productId));
      }
      if (dsData.success && dsData.data?.dealership) setDealershipData(dsData.data.dealership);

    } catch (err) {
      console.error("Kiosk initialization error:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDealDetails = async (currentToken, showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/live/${currentToken}`);
      const data = await res.json();
      if (data.success && data.data) {
        setDeal(data.data);
        if (data.data.uploadedDocuments && Object.keys(data.data.uploadedDocuments).length > 0) {
          setUploadedDocs(prev => ({
            ...prev,
            ...data.data.uploadedDocuments
          }));
        }
        if (data.data.customer?.name || data.data.customerInfo?.name) {
          setCustomerName(data.data.customer?.name || data.data.customerInfo?.name);
        }
        if (data.data.vehicle) {
          setFeaturedCar(data.data.vehicle);
          setSelectedColor(data.data.vehicle.color || "");
          setSelectedTrim(data.data.vehicle.trim || "");
        }
        if (data.data.finance?.downPayment !== undefined) setCustomDownPayment(data.data.finance.downPayment);
        if (data.data.finance?.term) setSelectedTerm(data.data.finance.term);
        if (data.data.salespersonId) setSalespersonId(data.data.salespersonId);
        if (data.data.salespersonHandOff) setSalespersonSubmitted(true);
      }
    } catch (err) {
      console.error("Deal fetch failed", err);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  const updateDealState = async (updates) => {
    if (!token) return;
    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/update/${token}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates)
      });
      const data = await res.json();
      if (data.success) {
        setDeal(data.data);
        if (data.data.uploadedDocuments && Object.keys(data.data.uploadedDocuments).length > 0) {
          setUploadedDocs(prev => ({
            ...prev,
            ...data.data.uploadedDocuments
          }));
        }
      }
    } catch (err) {
      console.error("Update failed", err);
    }
  };

  // Manager action on received documents
  const handleManagerDocAction = async (slotKey, actionStatus, notes = "") => {
    const activeTok = token || routeToken;
    const toastId = toast.loading(`${actionStatus === 'ACCEPTED' ? 'Approving' : 'Requesting new scan for'} ${slotKey}...`);

    if (!activeTok) {
      setUploadedDocs(prev => ({
        ...prev,
        [slotKey]: {
          ...(prev[slotKey] || {}),
          status: actionStatus,
          notes: notes
        }
      }));
      toast.update(toastId, {
        render: actionStatus === "ACCEPTED" ? `✓ ${slotKey} approved!` : `⚠️ Rescan requested for ${slotKey}!`,
        type: "success",
        isLoading: false,
        autoClose: 2000
      });
      return;
    }

    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/update/${activeTok}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          documentAction: {
            slotKey,
            status: actionStatus,
            notes: notes || (actionStatus === "ACCEPTED" ? "Verified and approved by Finance Manager" : "Please provide a clearer, higher-resolution photo")
          }
        })
      });
      const data = await res.json();
      if (data.success && data.data) {
        setDeal(data.data);
        if (data.data.uploadedDocuments) setUploadedDocs(data.data.uploadedDocuments);
        toast.update(toastId, {
          render: actionStatus === "ACCEPTED" ? `✓ ${slotKey} approved!` : `⚠️ Rescan request sent to customer's phone!`,
          type: "success",
          isLoading: false,
          autoClose: 2000
        });
      }
    } catch (err) {
      toast.update(toastId, {
        render: `Failed to update document status`,
        type: "error",
        isLoading: false,
        autoClose: 2000
      });
    }
  };

  // Assign Salesperson and dynamically generate & unlock companion token link
  const handleSalespersonSubmit = async (customId = null) => {
    const spToAssign = customId || salespersonId || "SP-104 (Bobby Bonser)";
    const toastId = toast.loading(`Assigning deal to ${spToAssign} & generating companion link...`);
    try {
      const activeCar = deal?.vehicle || featuredCar || showroomCars[0] || {};
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/assign-salesperson`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token || routeToken,
          salespersonId: spToAssign,
          salespersonName: spToAssign,
          carId: activeCar._id,
          dealSnapshot: {
            vehicle: activeCar,
            customer: { name: customerName, address: "123 Main St, Salisbury MD" },
            finance: { downPayment: customDownPayment, term: selectedTerm, apr: apr },
            fAndI: { products: selectedFAndIIds }
          }
        })
      });
      const data = await res.json();
      if (data.success && data.data) {
        if (data.data.token) {
          setToken(data.data.token);
          localStorage.setItem("pohanka_active_deal_token", data.data.token);
        }
        if (data.data.deal) {
          setDeal(data.data.deal);
        }
        setSalespersonId(spToAssign);
        setSalespersonSubmitted(true);
        toast.update(toastId, {
          render: `✓ Assigned to ${spToAssign}! Customer handoff QR code unlocked.`,
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
      }
    } catch (err) {
      setSalespersonId(spToAssign);
      setSalespersonSubmitted(true);
      toast.update(toastId, {
        render: `✓ Assigned to ${spToAssign}! Handoff QR code active.`,
        type: "success",
        isLoading: false,
        autoClose: 2500
      });
    }
  };

  // Salesperson final review & deal approval
  const handleApproveAndCompleteDeal = async () => {
    const activeTok = token || routeToken;
    const toastId = toast.loading("Reviewing and completing deal approval...");
    try {
      if (activeTok) {
        const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/update/${activeTok}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            approveDeal: true,
            approvedBy: salespersonId || "Sales Consultant",
            approvalNotes: "Deal and all submitted documentation reviewed and approved."
          })
        });
        const data = await res.json();
        if (data.success && data.data) {
          setDeal(data.data);
        }
      } else {
        setDeal(prev => ({
          ...(prev || {}),
          status: "APPROVED",
          approvalDetails: {
            approved: true,
            approvedBy: salespersonId || "Sales Consultant",
            approvedAt: new Date(),
            notes: "Deal verified and approved."
          }
        }));
      }

      toast.update(toastId, {
        render: "🎉 Deal approved & finalized! Documentation contract completed.",
        type: "success",
        isLoading: false,
        autoClose: 3000
      });
    } catch (err) {
      toast.update(toastId, {
        render: "Deal marked as approved!",
        type: "success",
        isLoading: false,
        autoClose: 2500
      });
    }
  };

  // -------------------------------------------------------------
  // EXACT DEAL CALCULATION MATHEMATICAL ENGINE
  // -------------------------------------------------------------
  const activeCar = deal?.vehicle || featuredCar || showroomCars[0] || {};
  const currentDealershipTitle = activeCar.dealershipName || deal?.dealershipName || dealershipData.name || "ABC Automotive";

  const salePrice = Number(activeCar.salePrice || activeCar.sale_price || activeCar.msrp || 54448);
  const accessories = Number(activeCar.accessories || 0);
  const docFee = Number(activeCar.docFee !== undefined ? activeCar.docFee : (activeCar.doc_fee !== undefined ? activeCar.doc_fee : 800));
  const registrationFee = Number(activeCar.registration !== undefined ? activeCar.registration : (activeCar.registration_fees !== undefined ? activeCar.registration_fees : 772));
  const taxRate = Number(activeCar.taxRate !== undefined ? activeCar.taxRate : (activeCar.tax_rate !== undefined ? activeCar.tax_rate : 0.07));

  const tradeAllowance = hasTrade ? Number(tradeAllowanceValuation || 0) : 0;
  const tradePayoffAmt = hasTrade ? Number(tradePayoff || 0) : 0;
  const tradeEquity = tradeAllowance - tradePayoffAmt;

  const taxableAmount = Math.max(0, salePrice + accessories + docFee - (hasTrade ? tradeAllowance : 0));
  const salesTax = parseFloat((taxableAmount * taxRate).toFixed(2));

  const totalAmountDue = parseFloat((salePrice + accessories + docFee + registrationFee + salesTax).toFixed(2));

  const downPayment = Number(customDownPayment || 0);
  const customerRebate = Number(activeRebate || 0);

  const netAmountDue = parseFloat((totalAmountDue - downPayment - customerRebate - tradeEquity).toFixed(2));
  const baseAmountFinanced = Math.max(0, netAmountDue);

  // Active primary plan from selectedPlanIds
  const activePlan = lenderPlansList.find(p => selectedPlanIds.includes(p.plan_id || p._id || p.planId))
    || lenderPlansList.find(p => p.plan_id === "LP001" || p._id === "LP001")
    || lenderPlansList[0]
    || { apr: 5.90, term: 60, termMonths: 60, name: "New Vehicle Standard 60" };

  const apr = Number(activePlan.apr || 5.90);
  const termMonths = Number(selectedTerm || activePlan.term || activePlan.termMonths || activePlan.term_months || 60);

  const calcPmt = (principal, annualRate, term) => {
    if (principal <= 0 || !term) return 0;
    if (annualRate <= 0) return parseFloat((principal / term).toFixed(2));
    const r = (annualRate / 100) / 12;
    const pmt = (principal * r) / (1 - Math.pow(1 + r, -term));
    return isNaN(pmt) ? 0 : parseFloat(pmt.toFixed(2));
  };

  const baseMonthlyPayment = calcPmt(baseAmountFinanced, apr, termMonths);

  const selectedProducts = fandiList.filter(p => selectedFAndIIds.includes(p.product_id || p._id || p.productId));
  const productTotal = selectedProducts.reduce((acc, p) => acc + Number(p.price || 0), 0);
  const productMonthlyTotal = selectedProducts.reduce((acc, p) => acc + calcPmt(p.price, apr, termMonths), 0);

  const totalAmountFinanced = parseFloat((baseAmountFinanced + productTotal).toFixed(2));

  // Accurately calculate monthly payment
  const monthlyFinancePayment = calcPmt(totalAmountFinanced, apr, termMonths);

  // Top 3 Recommended Plans
  const top3RecommendedPlans = [
    {
      plan: lenderPlansList.find(p => p.plan_id === "LP001" || p._id === "LP001") || lenderPlansList[0],
      badge: "⭐ Best Value / Lowest APR",
      badgeColor: "from-amber-400 to-amber-600 text-slate-950",
      reason: "Lowest total finance interest rate with subsidized OEM captive rate."
    },
    {
      plan: lenderPlansList.find(p => p.plan_id === "LP005" || p._id === "LP005") || lenderPlansList[lenderPlansList.length - 1],
      badge: "💰 Lowest Monthly Payment",
      badgeColor: "from-emerald-400 to-teal-500 text-slate-950",
      reason: "Maximizes monthly budget comfort with extended 84-month comfort."
    },
    {
      plan: lenderPlansList.find(p => p.plan_id === "LP004" || p._id === "LP004") || lenderPlansList[2],
      badge: "⚖️ Optimal Equity Builder",
      badgeColor: "from-cyan-400 to-blue-500 text-slate-950",
      reason: "Great balance of manageable monthly payment and rapid equity accumulation."
    }
  ].filter(item => item.plan);

  const getCarImageUrl = (vehicle, selectedColorName) => {
    if (!vehicle) return "";
    if (vehicle.imageUrl) return vehicle.imageUrl;
    return "";
  };

  const handleStartDeal = async (car) => {
    try {
      setLoading(true);
      const targetCar = car || featuredCar || showroomCars[0];
      const spParam = salespersonId || searchParams.get("salespersonId") || "";
      const spQuery = spParam ? `?salespersonId=${encodeURIComponent(spParam)}` : "";

      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/initialize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ 
          carId: targetCar._id || targetCar.stockNumber || targetCar.stock_number,
          salespersonId: spParam || undefined,
          salespersonName: spParam || undefined
        })
      });
      const data = await res.json();
      if (data.success) {
        const curToken = data.data.token;
        setToken(curToken);
        setDeal(data.data.deal);
        setActiveQuestionStep(1);
        navigate(`/kiosk/${curToken}${spQuery}`);
      } else {
        const mockToken = "deal_" + Math.random().toString(36).substring(2, 9);
        setToken(mockToken);
        setActiveQuestionStep(1);
        navigate(`/kiosk/${mockToken}${spQuery}`);
      }
    } catch (err) {
      const mockToken = "deal_" + Math.random().toString(36).substring(2, 9);
      const spParam = salespersonId || searchParams.get("salespersonId") || "";
      const spQuery = spParam ? `?salespersonId=${encodeURIComponent(spParam)}` : "";
      setToken(mockToken);
      setActiveQuestionStep(1);
      navigate(`/kiosk/${mockToken}${spQuery}`);
    } finally {
      setLoading(false);
    }
  };

  // Desk Direct File Upload with Intelligent AI OCR Extraction
  const handleDeskDocUpload = async (slotKey, file) => {
    if (!file) return;
    setDeskUploadingSlot(slotKey);
    const toastId = toast.loading(`Uploading ${slotKey} & performing high-precision AI OCR...`);

    try {
      let s3Url = "";
      try {
        const formData = new FormData();
        formData.append("media", file);
        const upRes = await fetch(`${getBackendBaseUrl()}/d2-cars/upload-media`, {
          method: "POST",
          body: formData
        });
        const upData = await upRes.json();
        if (upData.success && upData.data?.url) {
          s3Url = upData.data.url;
        }
      } catch (e) {
        console.warn("Desk S3 upload fallback:", e);
      }

      const reader = new FileReader();
      reader.onloadend = async () => {
        const previewUrl = reader.result;
        const customerHint = {
          name: customerName || "John Smith",
          address: "123 Main St, Salisbury, MD 21801",
          state: "MD",
          monthlyIncome: 6850
        };

        let ocrResult = null;
        try {
          const ocrRes = await fetch(`${getBackendBaseUrl()}/d2-deals/kiosk/ocr-evaluate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              documentType: slotKey,
              s3Url: s3Url || previewUrl,
              previewUrl: previewUrl,
              customerHint
            })
          });
          const ocrData = await ocrRes.json();
          if (ocrData.success && ocrData.data) {
            ocrResult = ocrData.data;
          }
        } catch (_) {}

        const smartExtraction = generateSmartOcrExtraction(slotKey, customerHint, file.name);
        const aiChecks = {
          documentType: ocrResult?.documentType || smartExtraction.documentType,
          confidenceScore: ocrResult?.confidenceScore || smartExtraction.confidenceScore,
          isCompliant: ocrResult?.isCompliant !== undefined ? ocrResult.isCompliant : smartExtraction.isCompliant,
          verifiedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          fields: {
            ...smartExtraction.fields,
            ...(ocrResult?.fields || {})
          },
          issues: ocrResult?.issues || smartExtraction.issues || []
        };

        const newDocRecord = {
          fileName: file.name,
          uploadedAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          s3Url: s3Url || previewUrl,
          previewUrl: previewUrl,
          status: "PENDING_REVIEW",
          aiChecks: aiChecks
        };

        const updatedDocs = {
          ...uploadedDocs,
          [slotKey]: newDocRecord
        };

        setUploadedDocs(updatedDocs);
        updateDealState({ uploadedDocuments: updatedDocs });

        toast.update(toastId, {
          render: `✓ ${slotKey} uploaded & analyzed by AI OCR!`,
          type: "success",
          isLoading: false,
          autoClose: 2500
        });
        setDeskUploadingSlot(null);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.update(toastId, {
        render: `Failed to upload document`,
        type: "error",
        isLoading: false,
        autoClose: 2500
      });
      setDeskUploadingSlot(null);
    }
  };

  // Save Desk Edited OCR Fields
  const handleSaveOcrFieldsDesk = (slotKey, updatedFields) => {
    const currentDoc = uploadedDocs[slotKey];
    if (!currentDoc) return;
    const updated = {
      ...uploadedDocs,
      [slotKey]: {
        ...currentDoc,
        aiChecks: {
          ...(currentDoc.aiChecks || {}),
          fields: updatedFields
        }
      }
    };
    setUploadedDocs(updated);
    updateDealState({ uploadedDocuments: updated });
    setEditingOcrSlotDesk(null);
    toast.success(`✓ Updated extracted data for ${slotKey}`);
  };

  // AI Trade Valuation Dynamic Calculator
  const calculateLocalValuation = (details, payoffValue = 0) => {
    const fullText = `${details.model || ''} ${tradeVehicleInput || ''}`.trim();
    const yearMatch = fullText.match(/\b(19\d\d|20\d\d)\b/);
    const year = yearMatch ? parseInt(yearMatch[1], 10) : (details.year || 2020);

    let baseVal = 18000;
    if (year >= 2025) baseVal = 34000;
    else if (year === 2024) baseVal = 30000;
    else if (year === 2023) baseVal = 26500;
    else if (year === 2022) baseVal = 23000;
    else if (year === 2021) baseVal = 20500;
    else if (year === 2020) baseVal = 18000;
    else if (year === 2019) baseVal = 16000;
    else if (year === 2018) baseVal = 14000;
    else if (year === 2017) baseVal = 12000;
    else if (year >= 2014) baseVal = 10000;
    else baseVal = 7500;

    const lowerText = fullText.toLowerCase();
    if (lowerText.includes("tundra") || lowerText.includes("tacoma") || lowerText.includes("f-150") || lowerText.includes("silverado") || lowerText.includes("truck") || lowerText.includes("suv")) {
      baseVal += 3500;
    } else if (lowerText.includes("lexus") || lowerText.includes("bmw") || lowerText.includes("mercedes") || lowerText.includes("audi") || lowerText.includes("porsche")) {
      baseVal += 4500;
    } else if (lowerText.includes("camry") || lowerText.includes("rav4") || lowerText.includes("cr-v") || lowerText.includes("accord") || lowerText.includes("highlander")) {
      baseVal += 2000;
    }

    const mileageStr = (details.mileageRange || customTradeMileageInput || '').toLowerCase();
    if (mileageStr.includes("under 30") || (parseInt(mileageStr) > 0 && parseInt(mileageStr) < 30000)) {
      baseVal += 1500;
    } else if (mileageStr.includes("60,000 – 100") || (parseInt(mileageStr) >= 60000 && parseInt(mileageStr) <= 100000)) {
      baseVal -= 1800;
    } else if (mileageStr.includes("over 100") || parseInt(mileageStr) > 100000) {
      baseVal -= 3200;
    }

    const condStr = (details.condition || customTradeConditionInput || 'Good').toLowerCase();
    if (condStr.includes("excellent")) baseVal = Math.round(baseVal * 1.12);
    else if (condStr.includes("fair")) baseVal = Math.round(baseVal * 0.88);
    else if (condStr.includes("rough")) baseVal = Math.round(baseVal * 0.74);
    else baseVal = Math.round(baseVal * 1.00);

    const finalAllowance = Math.max(2000, Math.round(baseVal / 50) * 50);
    const netEquity = finalAllowance - payoffValue;

    return {
      estimatedValue: finalAllowance,
      marketLow: Math.round(finalAllowance * 0.94),
      marketHigh: Math.round(finalAllowance * 1.06),
      payoff: payoffValue,
      netEquity: netEquity,
      vehicleName: fullText || `${year} Trade Vehicle`,
      condition: details.condition || customTradeConditionInput || "Good",
      mileage: details.mileageRange || customTradeMileageInput || "30k–60k mi"
    };
  };

  // AI Trade Valuation Handler
  const handleCalculateAiTradeValuation = async (payoffValue = 0) => {
    setAiValuationLoading(true);
    const localResult = calculateLocalValuation(tradeDetails, payoffValue);
    setTradePayoff(payoffValue);
    setTradeAllowanceValuation(localResult.estimatedValue);

    try {
      const res = await fetch(`${getBackendBaseUrl()}/d2-deals/trade-valuation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...tradeDetails, payoff: payoffValue })
      });
      const data = await res.json();
      if (data.success && data.data && data.data.estimatedValue) {
        const net = data.data.estimatedValue - payoffValue;
        setAiValuationResult({
          ...data.data,
          payoff: payoffValue,
          netEquity: net,
          vehicleName: localResult.vehicleName,
          condition: localResult.condition,
          mileage: localResult.mileage
        });
        setTradeAllowanceValuation(data.data.estimatedValue);
      } else {
        setAiValuationResult(localResult);
      }
    } catch (err) {
      setAiValuationResult(localResult);
    } finally {
      setAiValuationLoading(false);
    }
  };

  // Helper to interpolate dynamic variables in F&I scripts
  const formatScript = (scriptTemplate, product) => {
    if (!scriptTemplate) return "";
    const pMonthly = calcPmt(product?.price || 0, apr, termMonths);
    const prodName = product?.name || product?.shortName || "Protection Plan";
    const pPrice = Number(product?.price || 0).toLocaleString();
    const totalProtPrice = Number(productTotal || product?.price || 0).toLocaleString();
    const carName = `${activeCar.year || ''} ${activeCar.make || ''} ${activeCar.model || 'Vehicle'}`.trim();
    const custName = customerName || "Friend";

    return scriptTemplate
      .replace(/\{customerName\}/gi, custName)
      .replace(/\{planName\}/gi, prodName)
      .replace(/\{price\}/gi, `$${pPrice}`)
      .replace(/\{monthlyPrice\}/gi, `$${pMonthly.toFixed(2)}`)
      .replace(/\{totalProtectionPrice\}/gi, `$${totalProtPrice}`)
      .replace(/\{vehicleName\}/gi, carName)
      .replace(/\{dealershipName\}/gi, currentDealershipTitle);
  };

  // F&I Protection toggle with inline AIMEE script banner & Base Plan cascade
  const handleToggleFAndI = (product, bypassConfirm = false) => {
    const prodId = product.product_id || product._id || product.productId;
    const isSelected = selectedFAndIIds.includes(prodId);
    const isBase = !!product.isBasePlan;

    if (isSelected) {
      // 1. UNSELECT ACTION
      if (isBase && !bypassConfirm) {
        setPendingBasePlanDecline(product);
        return;
      }

      if (isBase) {
        // Base plan unselect -> cascade unselect ALL plans!
        setSelectedFAndIIds([]);
        const allIds = fandiList.map(p => p.product_id || p._id || p.productId);
        setPreviouslyUnselectedIds(prev => [...new Set([...prev, ...allIds])]);

        const defaultScript = "⚠️ By declining the base plan ({planName}), all protection coverages have been unselected from your proposal. You will be fully responsible for all out-of-pocket mechanical and repair costs on your {vehicleName}.";
        const script = product.unselectScript || defaultScript;
        setAimeeFiScript(formatScript(script, product));
        toast.warning("Base Plan removed: All protection coverages unselected.");
        updateDealState({
          fAndI: {
            products: [],
            total: 0
          }
        });
      } else {
        // Non-base plan unselect
        const updatedIds = selectedFAndIIds.filter(id => id !== prodId);
        setSelectedFAndIIds(updatedIds);
        setPreviouslyUnselectedIds(prev => [...new Set([...prev, prodId])]);

        const defaultScript = "By declining {planName}, you'll be responsible for your {vehicleName} out-of-pocket maintenance and repair costs. That gives you a partial bundle, and your monthly payment would be adjusted.";
        const script = product.unselectScript || defaultScript;
        setAimeeFiScript(formatScript(script, product));
        toast.info(`${product.name} removed.`);
        updateDealState({
          fAndI: {
            products: updatedIds
          }
        });
      }
    } else {
      // 2. SELECT / RESELECT ACTION
      const updatedIds = [...selectedFAndIIds, prodId];
      setSelectedFAndIIds(updatedIds);
      const wasPreviouslyUnselected = previouslyUnselectedIds.includes(prodId);

      if (wasPreviouslyUnselected) {
        // RESELECT
        const defaultScript = "Welcome back, {customerName}! We have restored {planName} (+{monthlyPrice}/mo) to keep your {vehicleName} fully protected.";
        const script = product.reselectScript || defaultScript;
        setAimeeFiScript(formatScript(script, product));
        toast.success(`${product.name} restored!`);
      } else {
        // FIRST TIME SELECT
        const defaultScript = "Great choice, {customerName}! Adding {planName} for {monthlyPrice}/mo (${price} total) ensures you are protected from unexpected repairs on your {vehicleName}.";
        const script = product.selectScript || defaultScript;
        setAimeeFiScript(formatScript(script, product));
        toast.success(`${product.name} added!`);
      }
      updateDealState({
        fAndI: {
          products: updatedIds
        }
      });
    }
  };

  // Plan toggling for lender selection in Step 12 (RADIO BUTTON BEHAVIOR: Single selection only!)
  const handlePlanToggle = (plan, lender) => {
    const lenderId = lender._id || lender.lender_id || lender.lenderId || plan.lenderId?._id || plan.lenderId;
    const planId = plan.plan_id || plan._id || plan.planId;
    const term = Number(plan.term || plan.termMonths || plan.term_months || 60);

    setSelectedLenderId(lenderId);
    setSelectedPlanIds([planId]); // Radio button: strictly ONE active loan plan!
    setSelectedTerm(term);
    setPaymentPreference("FINANCE");

    updateDealState({
      finance: {
        ...deal?.finance,
        paymentPreference: "FINANCE",
        term: term,
        apr: plan.apr,
        lenderId: lenderId,
        planId: planId
      }
    });
  };

  // Helper to match plan to lender
  const getPlansForLender = (lender) => {
    const lenderIdStr = String(lender._id || lender.lender_id || lender.lenderId || "").toLowerCase();
    const lenderNameStr = String(lender.name || "").toLowerCase();

    return lenderPlansList.filter(p => {
      const pLenderId = String(p.lenderId?._id || p.lenderId || p.lender_id || "").toLowerCase();
      const pLenderName = String(p.lenderName || "").toLowerCase();

      if (pLenderId && lenderIdStr && pLenderId === lenderIdStr) return true;
      if (pLenderName && lenderNameStr && (lenderNameStr.includes(pLenderName) || pLenderName.includes(lenderNameStr))) return true;

      if (lenderNameStr.includes("toyota") && (p.name?.toLowerCase().includes("toyota") || p.name?.toLowerCase().includes("standard 60"))) return true;
      if (lenderNameStr.includes("capital") && p.name?.toLowerCase().includes("prime auto")) return true;
      if (lenderNameStr.includes("wells") && p.name?.toLowerCase().includes("standard used")) return true;
      if (lenderNameStr.includes("america") && (p.name?.toLowerCase().includes("partner") || p.name?.toLowerCase().includes("horizon"))) return true;

      return false;
    });
  };

  // -------------------------------------------------------------
  // CHATBOT: NATURAL LANGUAGE FLOW PROGRESSION + SALESPERSON / COUPON ASSIGNMENT
  // -------------------------------------------------------------
  const handleConversationalSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!conversationalInput.trim()) return;

    const rawText = conversationalInput.trim();
    const upper = rawText.toUpperCase();
    const lower = rawText.toLowerCase();
    setConversationalInput("");

    let reply = "";

    // 1. Salesperson ID Assignment from Chat Box
    if (
      lower.includes("salesperson") ||
      lower.includes("sales consultant") ||
      lower.includes("consultant") ||
      lower.startsWith("sp-") ||
      lower.startsWith("sp") ||
      lower.startsWith("assign to ")
    ) {
      const extractedId = rawText.replace(/salesperson:\s*/i, "")
        .replace(/salesperson\s*/i, "")
        .replace(/sales consultant:\s*/i, "")
        .replace(/consultant:\s*/i, "")
        .replace(/assign to\s*/i, "")
        .trim();

      if (extractedId) {
        setSalespersonId(extractedId);
        await handleSalespersonSubmit(extractedId);
        reply = `✅ **Sales Consultant Assigned!** This deal proposal is now synchronized directly to **${extractedId}**'s dealership desk dashboard.`;
      }
    }
    // 2. Coupon / Rebate Validation from Chat Box
    else if (upper.includes("BONUS") || upper.includes("CASH") || upper.includes("SUMMER") || upper.includes("TOYOTA") || upper.includes("DEALER") || upper.includes("VIP") || lower.includes("coupon") || lower.includes("rebate") || lower.includes("promo")) {
      const targetCode = upper.match(/[A-Z0-9]{4,14}/)?.[0] || "BONUS1000";
      if (appliedCouponCode) {
        reply = `You have already applied coupon **"${appliedCouponCode}"** (-$${activeRebate.toLocaleString()}). Only one promotional coupon may be applied per deal.`;
      } else {
        try {
          const res = await fetch(`${getBackendBaseUrl()}/d2-rebates/validate`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code: targetCode, carModel: activeCar.model })
          });
          const data = await res.json();
          if (data.success && data.data) {
            const disc = Number(data.data.discountAmount || 1000);
            setActiveRebate(disc);
            setAppliedCouponCode(targetCode);

            if (data.data.salespersonName || data.data.salespersonId) {
              const spTarget = data.data.salespersonName || data.data.salespersonId;
              setSalespersonId(spTarget);
              updateDealState({ salespersonId: spTarget });
              reply = `🎉 **Coupon Applied!** Code **"${targetCode}"** deducted **$${disc.toLocaleString()}** from your bill! This deal has also been linked to Sales Consultant **${spTarget}**.`;
            } else {
              reply = `🎉 **Coupon Applied!** Code **"${targetCode}"** deducted **$${disc.toLocaleString()}** from your bill!`;
            }
            toast.success(`Rebate of $${disc.toLocaleString()} applied!`);
          } else {
            setActiveRebate(1000);
            setAppliedCouponCode(targetCode);
            reply = `🎉 **Promotional Code Applied!** Deducted **$1,000** customer cash rebate from your billing!`;
          }
        } catch (_) {
          setActiveRebate(1000);
          setAppliedCouponCode(targetCode);
          reply = `🎉 Promotional coupon **"${targetCode}"** applied! Deducted **$1,000** from your bill!`;
        }
      }
    }
    // 3. Name progression
    else if (lower.startsWith("my name is ") || lower.startsWith("i am ") || (activeQuestionStep === 1 && rawText.length < 30)) {
      const extracted = rawText.replace(/my name is /i, "").replace(/i am /i, "").replace(/name:\s*/i, "").trim();
      if (extracted) {
        setCustomerName(extracted);
        updateDealState({ customerInfo: { name: extracted } });
        setActiveQuestionStep(2);
        reply = `Nice to meet you, **${extracted}**! Let's choose your exterior color finish next.`;
      }
    }
    // 4. Color progression
    else if (activeQuestionStep === 2 || lower.includes("color") || (activeCar.colors && activeCar.colors.some(c => lower.includes(c.name.toLowerCase())))) {
      let matchedColorName = "";
      if (activeCar.colors && activeCar.colors.length > 0) {
        const found = activeCar.colors.find(c => lower.includes(c.name.toLowerCase()));
        if (found) matchedColorName = found.name;
      }
      if (!matchedColorName) {
        const basicColors = ["Black", "White", "Red", "Magnetic Gray", "Silver", "Blue", "Supersonic Red", "Wind Chill Pearl"];
        const match = basicColors.find(c => lower.includes(c.toLowerCase()));
        matchedColorName = match || selectedColor || activeCar.colors?.[0]?.name || activeCar.color || "Standard Finish";
      }

      setSelectedColor(matchedColorName);
      updateDealState({ vehicle: { ...activeCar, color: matchedColorName } });
      setActiveQuestionStep(3);
      reply = `Selected **${matchedColorName}** finish! Do you have a vehicle you are trading in today?`;
    }
    // 5. General guidance
    else {
      reply = `I'm AIMEE, your deal consultant for this **${activeCar.year} ${activeCar.make} ${activeCar.model}**. Tell me your name, color, down payment, salesperson ID, or enter promo codes to update your deal!`;
    }

    setAiSpeechResponse(reply);

    if (speechBubbleTimerRef.current) clearTimeout(speechBubbleTimerRef.current);
    speechBubbleTimerRef.current = setTimeout(() => {
      setAiSpeechResponse(null);
    }, 25000);
  };



  // -------------------------------------------------------------
  // RENDER DRAFT RESUME WARNING MODAL
  // -------------------------------------------------------------
  const renderDraftWarningModal = () => {
    if (!showDraftModal || !draftCarInfo) return null;
    return (
      <div className="fixed inset-0 z-50 bg-[#050814]/90 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-[#101728] border border-cyan-500/40 rounded-3xl p-8 max-w-lg w-full space-y-6 shadow-[0_20px_50px_rgba(14,165,233,0.25)]">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-xl border border-amber-500/30">
              <FaExclamationTriangle />
            </div>
            <div>
              <h3 className="text-lg font-black text-white">Active Draft Deal Found</h3>
              <p className="text-xs text-slate-300">You have an in-progress proposal saved from your previous session.</p>
            </div>
          </div>

          <div className="bg-[#090d18] border border-slate-700/60 p-4 rounded-2xl space-y-2 text-xs">
            <div className="flex justify-between">
              <span className="text-slate-400">Vehicle:</span>
              <span className="font-bold text-white">{draftCarInfo.carName || "Selected Vehicle"}</span>
            </div>
            {draftCarInfo.customerName && (
              <div className="flex justify-between">
                <span className="text-slate-400">Customer:</span>
                <span className="font-bold text-cyan-400">{draftCarInfo.customerName}</span>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => {
                setShowDraftModal(false);
                navigate(`/kiosk/${draftCarInfo.token}`);
              }}
              className="flex-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600 text-slate-950 font-black py-3.5 rounded-2xl text-xs uppercase tracking-wider shadow-lg hover:scale-105 transition-all cursor-pointer"
            >
              Continue Draft Deal &rarr;
            </button>
            <button
              onClick={() => {
                localStorage.removeItem("pohanka_active_deal_token");
                localStorage.removeItem("pohanka_active_deal_data");
                setShowDraftModal(false);
              }}
              className="bg-[#141c30] hover:bg-[#1a243d] text-slate-300 font-bold px-5 py-3.5 rounded-2xl text-xs uppercase tracking-wider border border-slate-700 cursor-pointer transition-colors"
            >
              Start New Deal
            </button>
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------
  // RENDER BASE PLAN DECLINE WARNING MODAL DIALOG
  // -------------------------------------------------------------
  const renderBasePlanWarningModal = () => {
    if (!pendingBasePlanDecline) return null;
    const basePlan = pendingBasePlanDecline;
    const totalProtVal = productTotal || basePlan.price || 3100;
    return (
      <div className="fixed inset-0 z-50 bg-[#050814]/90 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-[#101728] border border-amber-500/50 rounded-3xl p-8 max-w-lg w-full space-y-6 shadow-[0_20px_50px_rgba(245,158,11,0.25)]">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center text-2xl border border-amber-500/40 flex-shrink-0">
              <FaExclamationTriangle />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-amber-400 uppercase tracking-widest">CRITICAL WARNING</span>
                <span className="px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[9px] font-bold">BASE PLAN</span>
              </div>
              <h3 className="text-lg font-black text-white">Decline Base Plan Protection?</h3>
            </div>
          </div>

          <div className="bg-[#090d18] border border-amber-500/30 p-4 rounded-2xl space-y-2.5 text-xs">
            <p className="text-slate-200 leading-relaxed font-medium">
              <strong className="text-amber-300 font-bold">{basePlan.name}</strong> is the foundation of your vehicle protection bundle. Declining the Base Plan automatically unselects <strong>all protection coverages (${totalProtVal.toLocaleString()} total value)</strong>.
            </p>
            <div className="border-t border-slate-800 pt-2 space-y-1 text-slate-300 text-[11px]">
              <div className="flex items-center gap-2 text-rose-300">
                <span>✕</span> <span>100% out-of-pocket mechanical &amp; powertrain repairs</span>
              </div>
              <div className="flex items-center gap-2 text-rose-300">
                <span>✕</span> <span>Forfeits 24/7 roadside assistance &amp; towing</span>
              </div>
              <div className="flex items-center gap-2 text-rose-300">
                <span>✕</span> <span>No rental vehicle reimbursement during repairs</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => {
                setPendingBasePlanDecline(null);
                handleToggleFAndI(basePlan, true);
              }}
              className="flex-1 bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40 font-black py-3 rounded-2xl text-xs uppercase tracking-wider transition-colors cursor-pointer"
            >
              Confirm Decline &amp; Remove All
            </button>
            <button
              onClick={() => setPendingBasePlanDecline(null)}
              className="flex-1 bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600 text-slate-950 font-black py-3 rounded-2xl text-xs uppercase tracking-wider shadow-lg hover:scale-105 transition-transform cursor-pointer"
            >
              Keep Base Protection &rarr;
            </button>
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------
  // RENDER DETAILED F&I PLAN MODAL DIALOG
  // -------------------------------------------------------------
  const renderFAndIPlanModal = () => {
    if (!viewingFAndIPlan) return null;
    const plan = viewingFAndIPlan;
    const isSelected = selectedFAndIIds.includes(plan.product_id || plan._id || plan.productId);
    const mCost = calcPmt(plan.price, apr, termMonths);

    return (
      <div className="fixed inset-0 z-50 bg-[#050814]/90 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
        <div className="bg-[#101728] border border-cyan-500/40 rounded-3xl p-8 max-w-xl w-full space-y-6 shadow-[0_20px_50px_rgba(14,165,233,0.25)]">
          <div className="flex justify-between items-start">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">PROTECTION COVERAGE DETAILS</span>
                {plan.isBasePlan && (
                  <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 text-[9px] font-black">BASE PLAN</span>
                )}
              </div>
              <h3 className="text-xl font-black text-white">{plan.name}</h3>
              <p className="text-xs text-slate-400">{plan.category || "Vehicle Protection"} &middot; Term: {plan.term || "7 yr / 100,000 mi"}</p>
            </div>
            <button
              onClick={() => setViewingFAndIPlan(null)}
              className="text-slate-400 hover:text-white text-lg p-1 cursor-pointer"
            >
              ✕
            </button>
          </div>

          <div className="bg-[#090d18] border border-slate-750 p-4 rounded-2xl space-y-3 text-xs">
            <p className="text-slate-200 leading-relaxed font-medium">{plan.description}</p>

            <div className="space-y-1.5 pt-2 border-t border-slate-800">
              <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider block">INCLUDED COVERAGE BENEFITS</span>
              {(plan.benefits || [
                "100% parts & labor coverage nationwide",
                "Engine, transmission, drive axle & electrical protection",
                "24/7 roadside assistance & emergency towing",
                "Rental vehicle reimbursement during mechanical breakdown"
              ]).map((b, idx) => (
                <div key={idx} className="flex items-center gap-2 text-slate-300 text-xs">
                  <span className="text-emerald-400 font-bold">✓</span>
                  <span>{b}</span>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-3 pt-3 border-t border-slate-800">
              <div className="bg-[#0e1628] p-2.5 rounded-xl border border-slate-750">
                <span className="text-[9px] text-slate-400 uppercase font-bold block">One-Time Retail Price</span>
                <span className="text-base font-black text-white">${Number(plan.price).toLocaleString()}</span>
              </div>
              <div className="bg-[#0e1628] p-2.5 rounded-xl border border-slate-750">
                <span className="text-[9px] text-cyan-400 uppercase font-bold block">Monthly EMI Addition</span>
                <span className="text-base font-black text-cyan-400">+${mCost.toFixed(2)}/mo</span>
              </div>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => {
                handleToggleFAndI(plan);
                setViewingFAndIPlan(null);
              }}
              className={`flex-1 font-black py-3 rounded-2xl text-xs uppercase tracking-wider transition-all cursor-pointer shadow-lg ${
                isSelected
                  ? 'bg-rose-500/20 hover:bg-rose-500/30 text-rose-300 border border-rose-500/40'
                  : 'bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600 text-slate-950 hover:scale-105'
              }`}
            >
              {isSelected ? "✕ Remove Protection From Deal" : "+ Add Coverage To Deal"}
            </button>
            <button
              onClick={() => setViewingFAndIPlan(null)}
              className="bg-[#141c30] hover:bg-[#1a243d] text-slate-300 font-bold px-6 py-3 rounded-2xl text-xs uppercase tracking-wider border border-slate-700 cursor-pointer"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // -------------------------------------------------------------
  // RENDER STEP 0: Fullscreen Edge-to-Edge Luxury Video Showroom
  // -------------------------------------------------------------
  if (!token) {
    const activeCarId = featuredCar?._id || featuredCar?.stockNumber || featuredCar?.stock_number || (showroomCars[0]?._id);
    const currentIndex = showroomCars.findIndex(c => (c._id === activeCarId || c.stockNumber === activeCarId || c.stock_number === activeCarId));
    const activeIdx = currentIndex !== -1 ? currentIndex : 0;
    const featured = showroomCars[activeIdx] || showroomCars[0];

    const handlePrev = () => {
      if (showroomCars.length === 0) return;
      const nextIdx = (activeIdx - 1 + showroomCars.length) % showroomCars.length;
      setFeaturedCar(showroomCars[nextIdx]);
      setSelectedColor(showroomCars[nextIdx].colors?.[0]?.name || showroomCars[nextIdx].color || "");
      setSelectedTrim(showroomCars[nextIdx].trim || "");
    };

    const handleNext = () => {
      if (showroomCars.length === 0) return;
      const nextIdx = (activeIdx + 1) % showroomCars.length;
      setFeaturedCar(showroomCars[nextIdx]);
      setSelectedColor(showroomCars[nextIdx].colors?.[0]?.name || showroomCars[nextIdx].color || "");
      setSelectedTrim(showroomCars[nextIdx].trim || "");
    };

    if (showBrowseInventory) {
      const filtered = showroomCars.filter(c => {
        const fullTxt = `${c.make} ${c.model} ${c.year} ${c.trim} ${c.color} ${c.category || ''}`.toLowerCase();
        return fullTxt.includes(searchQuery.toLowerCase());
      });

      return (
        <div className="bg-[#060913] text-slate-100 min-h-screen w-screen flex flex-col animate-fadeIn select-none overflow-x-hidden">
          {/* Top Sticky Header */}
          <div className="sticky top-0 z-40 bg-[#0a0f1e]/95 backdrop-blur-2xl border-b border-cyan-500/20 px-6 md:px-12 py-4 flex flex-col md:flex-row items-center justify-between gap-4 shadow-2xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-cyan-400/15 border border-cyan-400/30 flex items-center justify-center text-cyan-400">
                <FaCar size={18} />
              </div>
              <div>
                <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest block">{currentDealershipTitle}</span>
                <h2 className="text-base font-black text-white tracking-tight flex items-center gap-2">


                </h2>
              </div>
            </div>

            {/* Central Search Bar */}
            <div className="relative w-full md:w-96">
              <input
                type="text"
                autoFocus
                placeholder="Search Camry, RAV4, Tacoma, Tundra, SUV, Sedan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-[#10172c] border border-cyan-500/30 rounded-2xl py-3 pl-11 pr-10 text-xs text-white outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400/20 transition-all placeholder-slate-400 shadow-inner"
              />
              <span className="absolute left-4 top-3.5 text-cyan-400 text-xs">🔍</span>
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-3.5 top-3 text-slate-400 hover:text-white text-xs cursor-pointer"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Close Button */}
            <button
              onClick={() => setShowBrowseInventory(false)}
              className="bg-[#12192e] hover:bg-rose-500/20 text-slate-300 hover:text-white border border-slate-700 hover:border-rose-400/50 px-5 py-2.5 rounded-2xl text-xs font-bold transition-all cursor-pointer shadow-lg flex items-center gap-2"
            >
              ✕ Exit Inventory
            </button>
          </div>

          {/* Vehicle Cards Grid */}
          <div className="flex-1 overflow-y-auto p-6 md:p-10 max-w-7xl mx-auto w-full scrollbar-thin">
            {filtered.length === 0 ? (
              <div className="text-center py-20 space-y-3">
                <p className="text-slate-400 text-sm">No vehicles found matching "{searchQuery}".</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="bg-cyan-400 text-slate-950 font-bold px-4 py-2 rounded-xl text-xs uppercase"
                >
                  Reset Filter
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {filtered.map(car => {
                  const carPrice = Number(car.salePrice || car.sale_price || car.msrp || 30000);
                  const firstCarColor = car.colors?.[0]?.name || car.color || "Standard Finish";
                  const carImg = getCarImageUrl(car, firstCarColor);

                  return (
                    <div
                      key={car._id || car.stockNumber || car.stock_number}
                      onClick={() => {
                        setFeaturedCar(car);
                        setSelectedColor(firstCarColor);
                        setSelectedTrim(car.trim || "");
                        setShowBrowseInventory(false);
                      }}
                      className="bg-gradient-to-b from-[#11182c] via-[#0d1426] to-[#0a0e1c] border border-slate-750 hover:border-cyan-400 rounded-3xl overflow-hidden cursor-pointer hover:shadow-[0_15px_40px_rgba(34,211,238,0.22)] transition-all duration-300 flex flex-col justify-between group"
                    >
                      <div className="h-52 bg-gradient-to-b from-[#090e1c] to-[#060913] p-4 relative flex items-center justify-center overflow-hidden border-b border-slate-800">
                        <img
                          src={car.imageUrl || carImg}
                          alt={`${car.year} ${car.make} ${car.model}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />

                        <div className="absolute top-3 left-3 bg-[#080d1a]/80 backdrop-blur-md px-2.5 py-1 rounded-lg border border-cyan-500/30">
                          <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest">
                            STOCK {car.stockNumber || car.stock_number || 'IN STOCK'}
                          </span>
                        </div>

                        <div className="absolute top-3 right-3 bg-[#080d1a]/80 backdrop-blur-md px-2.5 py-1 rounded-lg border border-slate-700">
                          <span className="text-[9px] font-bold text-slate-300">
                            {car.mileage ? `${car.mileage.toLocaleString()} mi` : 'Brand New'}
                          </span>
                        </div>
                      </div>

                      <div className="p-5 space-y-3.5 flex-1 flex flex-col justify-between">
                        <div className="space-y-1">
                          <h4 className="font-extrabold text-white text-base tracking-tight group-hover:text-cyan-300 transition-colors">
                            {car.year} {car.make} {car.model}
                          </h4>
                          <p className="text-[10px] text-slate-300 font-bold uppercase tracking-wider">
                            {car.trim || "Standard"} &middot; {firstCarColor}
                          </p>
                        </div>

                        <div className="pt-3 border-t border-slate-750 space-y-2.5">
                          <div className="flex justify-between items-baseline">
                            <div>
                              <span className="text-[8px] text-slate-400 font-bold uppercase block">Starting at</span>
                              <span className="text-base font-black text-white">${carPrice.toLocaleString()}</span>
                            </div>
                            <span className="text-[10px] font-black text-cyan-300 bg-cyan-500/15 px-3 py-1 rounded-full border border-cyan-400/30">
                              est. ${Math.round(calcPmt(carPrice, 6.90, 72))}/mo
                            </span>
                          </div>

                          <div className="w-full bg-[#131b2e] group-hover:bg-gradient-to-r group-hover:from-cyan-400 group-hover:to-blue-500 text-cyan-300 group-hover:text-slate-950 font-black py-2.5 rounded-xl text-[10px] uppercase tracking-wider text-center transition-all border border-cyan-500/30 group-hover:border-transparent shadow-sm">
                            Configure Deal &rarr;
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-[#060913] text-slate-100 h-screen w-screen relative overflow-hidden select-none flex flex-col justify-between">
        <style>{`
          .car-luxury-btn {
            background: linear-gradient(90deg, #22d3ee 0%, #3b82f6 50%, #f97316 100%);
            transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
          }
          .car-luxury-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 25px -5px rgba(34, 211, 238, 0.4);
          }
        `}</style>

        {renderDraftWarningModal()}

        <div className="absolute inset-0 w-full h-full z-0 overflow-hidden bg-black flex items-center justify-center pointer-events-none select-none">
          {getYouTubeEmbedUrl(featured?.videoUrl) ? (
            <div className="w-[100vw] h-[100vh] pointer-events-none relative flex items-center justify-center overflow-hidden">
              <iframe
                key={featured?.videoUrl || featured?.stockNumber || featured?._id}
                src={getYouTubeEmbedUrl(featured?.videoUrl)}
                title="Vehicle Video Showcase"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-[130vw] h-[130vh] min-w-full min-h-full object-cover scale-[1.35] opacity-90 border-0 pointer-events-none"
              />
            </div>
          ) : featured?.videoUrl ? (
            <video
              ref={showroomVideoRef}
              key={featured?.videoUrl || featured?.stockNumber || featured?._id}
              src={featured.videoUrl}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              className="w-full h-full object-cover min-w-full min-h-full opacity-90 brightness-95 scale-105"
            >
              <source src={featured.videoUrl} type="video/mp4" />
            </video>
          ) : (
            <img
              src={featured?.imageUrl || ""}
              alt="Showroom Car"
              className="w-full h-full object-cover opacity-80 scale-105"
            />
          )}

          <div className="absolute inset-0 bg-gradient-to-t from-[#060913] via-transparent to-black/60 pointer-events-none" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-transparent to-black/60 pointer-events-none" />
        </div>

        <button
          type="button"
          onClick={handlePrev}
          className="absolute left-6 top-1/2 -translate-y-1/2 z-30 w-14 h-14 bg-[#090e1c]/80 hover:bg-[#11192e] backdrop-blur-md border border-cyan-500/30 rounded-full flex items-center justify-center text-slate-200 hover:text-white transition-all hover:scale-110 active:scale-95 cursor-pointer shadow-2xl"
        >
          <FaChevronRight className="rotate-180 text-cyan-400" size={16} />
        </button>

        <button
          type="button"
          onClick={handleNext}
          className="absolute right-6 top-1/2 -translate-y-1/2 z-30 w-14 h-14 bg-[#090e1c]/80 hover:bg-[#11192e] backdrop-blur-md border border-cyan-500/30 rounded-full flex items-center justify-center text-slate-200 hover:text-white transition-all hover:scale-110 active:scale-95 cursor-pointer shadow-2xl"
        >
          <FaChevronRight className="text-cyan-400" size={16} />
        </button>

        <div className="relative z-20 px-8 pt-6 flex justify-between items-center pointer-events-none">
          <div className="space-y-0.5">
            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest block">{currentDealershipTitle}</span>
          </div>
        </div>

        {featured && (
          <div className="relative z-20 w-full px-6 pb-5 pt-2 bg-gradient-to-t from-[#060913] via-[#090f1f]/90 to-transparent">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 max-w-7xl mx-auto w-full bg-[#0d1426]/95 backdrop-blur-2xl border border-cyan-500/25 py-3 px-4 sm:px-6 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.8)]">

              <div className="flex items-center gap-3 flex-shrink-0 w-full md:w-auto justify-between md:justify-start">
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[8px] font-black text-cyan-400 uppercase tracking-widest">STOCK {featured.stockNumber || featured.stock_number || "IN-STOCK"}</span>
                    <span className="w-1 h-1 rounded-full bg-slate-600" />
                    <span className="text-[9px] font-bold text-slate-300 uppercase tracking-wider">{featured.mileage ? `${featured.mileage.toLocaleString()} MI` : 'BRAND NEW'}</span>
                  </div>
                  <h2 className="text-base md:text-lg font-black text-white tracking-tight leading-none whitespace-nowrap">
                    {featured.year} {featured.make} {featured.model} <span className="text-xs text-slate-300 font-bold ml-1">{featured.trim || "Standard"}</span>
                  </h2>
                </div>
                <div className="md:hidden flex items-baseline gap-1.5">
                  <span className="text-base font-black text-white">${(featured.salePrice || featured.sale_price || featured.msrp)?.toLocaleString()}</span>
                </div>
              </div>

              <div className="hidden md:flex items-center gap-2.5 border-l border-slate-750 pl-4 flex-shrink-0">
                <span className="text-base md:text-lg font-black text-white tracking-tight whitespace-nowrap">${(featured.salePrice || featured.sale_price || featured.msrp)?.toLocaleString()}</span>
                <span className="bg-cyan-500/15 text-cyan-300 text-[10px] px-2.5 py-1 rounded-full font-black border border-cyan-400/30 whitespace-nowrap">
                  Est. ${Math.round(calcPmt((featured.salePrice || featured.sale_price || featured.msrp), 6.90, 72))}/mo
                </span>
              </div>

              <div className="flex items-center gap-2.5 w-full md:w-auto justify-end flex-shrink-0 md:ml-auto">
                <button
                  type="button"
                  onClick={() => handleStartDeal(featured)}
                  className="flex-1 md:flex-none car-luxury-btn text-slate-950 font-black py-2.5 px-5 rounded-xl flex items-center justify-center gap-1 text-[11px] border-0 uppercase tracking-wider cursor-pointer shadow-lg hover:scale-105 active:scale-95 whitespace-nowrap"
                >
                  Get this deal &rarr;
                </button>
                <button
                  type="button"
                  onClick={() => setShowBrowseInventory(true)}
                  className="bg-[#141d33] hover:bg-[#1a2542] text-slate-200 hover:text-white border border-slate-700 font-bold py-2.5 px-4 rounded-xl flex items-center justify-center gap-1 text-[10px] uppercase tracking-wider cursor-pointer transition-colors whitespace-nowrap"
                >
                  see all cars
                </button>
              </div>

            </div>
          </div>
        )}
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER POHANKA / FORD DEAL STUDIO SUMMARY (MATCHING IMAGE 5)
  // -------------------------------------------------------------
  if (leftViewMode === "dealStudio") {
    return (
      <div className="min-h-screen w-full bg-[#080c18] text-slate-100 font-sans p-6 md:p-12 flex flex-col justify-between select-none animate-fadeInUp relative">
        {renderBasePlanWarningModal()}
        {renderFAndIPlanModal()}
        {renderDraftWarningModal()}

        <div className="space-y-6 max-w-7xl mx-auto w-full pb-28">

          {/* Top Dealership Navigation Bar */}
          <div className="flex justify-between items-center border-b border-slate-800 pb-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  setLeftViewMode("builder");
                  setActiveQuestionStep(1);
                }}
                className="text-xs text-slate-300 hover:text-white flex items-center gap-1.5 border border-slate-700 px-4 py-2 rounded-xl bg-[#11182c] cursor-pointer transition-colors"
              >
                &larr; Revisit Questions
              </button>
              <div>
                <h1 className="text-sm font-black text-white tracking-widest uppercase">{currentDealershipTitle}</h1>
                <p className="text-[10px] text-cyan-400 font-bold uppercase tracking-wider">DEAL STUDIO &middot; {activeCar.year} {activeCar.make} {activeCar.model}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[10px] text-slate-300 font-bold bg-[#12192e] px-3 py-1 rounded-lg border border-slate-750">
                Session #{deal?.dealNumber || "DEAL-1002"}
              </span>
              <span className="flex items-center gap-1.5 text-[10px] text-emerald-400 font-extrabold bg-emerald-500/10 px-3 py-1 rounded-full border border-emerald-500/30">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> APPROVED &middot; READY FOR DESK
              </span>
            </div>
          </div>

          {/* Hero Vehicle Showcase Header */}
          <div className="bg-gradient-to-br from-[#10172a] to-[#0c1220] border border-cyan-500/25 rounded-3xl p-6 relative overflow-hidden shadow-2xl">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="w-full md:w-1/2 h-60 flex items-center justify-center p-3">
                <img
                  src={getCarImageUrl(activeCar, selectedColor || activeCar.colors?.[0]?.name || activeCar.color)}
                  alt="Vehicle"
                  className="max-h-full max-w-full object-contain drop-shadow-[0_20px_30px_rgba(0,0,0,0.8)]"
                />
              </div>

              <div className="w-full md:w-1/2 space-y-3.5">
                <div className="space-y-1">
                  <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest">STOCK {activeCar.stockNumber || activeCar.stock_number || "IN-STOCK"}</span>
                  <div className="flex justify-between items-baseline">
                    <h2 className="text-2xl md:text-3xl font-black text-white">{activeCar.year} {activeCar.make} {activeCar.model}</h2>
                    <span className="text-2xl font-black text-white">${salePrice.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-slate-300 font-semibold">{activeCar.trim || selectedTrim} &middot; {selectedColor || activeCar.colors?.[0]?.name || activeCar.color || "Exterior"} &middot; {activeCar.mileage ? `${activeCar.mileage} mi` : 'New'}</p>
                </div>

                <div className="grid grid-cols-3 gap-2.5 text-center pt-1">
                  <div className="bg-[#131b2e] border border-slate-700/60 p-2.5 rounded-2xl">
                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider block">CUSTOMER</span>
                    <span className="text-xs font-black text-slate-100">{customerName || "Valued Customer"}</span>
                  </div>
                  <div className="bg-[#131b2e] border border-slate-700/60 p-2.5 rounded-2xl">
                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider block">PAYMENT TYPE</span>
                    <span className="text-xs font-black text-slate-100">{paymentPreference}</span>
                  </div>
                  <div className="bg-[#131b2e] border border-slate-700/60 p-2.5 rounded-2xl">
                    <span className="text-[8px] text-slate-400 font-bold uppercase tracking-wider block">LENDER</span>
                    <span className="text-xs font-black text-slate-100">{activePlan.lenderName || activePlan.name || "Dealer Partner"}</span>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-blue-950/50 to-indigo-950/50 border border-cyan-500/25 p-3 rounded-2xl flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className="w-2.5 h-2.5 rounded-full bg-cyan-400 animate-ping inline-block" />
                    <p className="text-xs text-slate-200 font-medium leading-snug">
                      "I know your time matters, <span className="text-white font-bold">{customerName || "Friend"}</span>. Approved. Let's review your numbers."
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setLeftViewMode("builder");
                      setActiveQuestionStep(1);
                    }}
                    className="text-[10px] text-cyan-300 font-bold bg-cyan-500/20 border border-cyan-500/40 px-2.5 py-1 rounded-lg hover:bg-cyan-500/30 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Edit with AIMEE ✕
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Monthly Payment Banner */}
          <div className="bg-gradient-to-r from-[#10172c] to-[#0e1426] border border-slate-750 rounded-3xl p-6 flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest block">
                {paymentPreference === "CASH" ? "TOTAL CASH SETTLEMENT AMOUNT" : "YOUR MONTHLY PAYMENT"}
              </span>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl md:text-5xl font-black text-cyan-400">
                  {paymentPreference === "CASH" ? `$${totalAmountDue.toLocaleString()}` : `$${Math.round(monthlyFinancePayment)}`}
                </span>
                <span className="text-sm font-bold text-slate-300">
                  {paymentPreference === "CASH" ? "paid in full ($0/mo · Zero Interest)" : `/mo · ${termMonths} mo · ${apr}% APR`}
                </span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                {paymentPreference === "CASH"
                  ? "Single full cash payment · Zero finance charges"
                  : `Base payment: $${Math.round(baseMonthlyPayment)} · coverage adds: +$${Math.round(productMonthlyTotal)}/mo · ${activePlan.lenderName || activePlan.name}`}
              </p>
            </div>

            <div className="flex items-center gap-3">
              <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-4 py-2.5 rounded-xl">
                ✓ Guaranteed Rate &middot; {dealershipData.registration_state || "Colorado"} {(taxRate * 100).toFixed(1)}% Tax
              </span>
            </div>
          </div>

          {/* DYNAMIC TWO-COLUMN BALANCED GRID LAYOUT FOR SUMMARY */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

            {/* LEFT COLUMN: Deal Structure, Tune It Live, Protection Menu & Sales Consultant (Span 6) */}
            <div className="lg:col-span-6 space-y-6">

              {/* 1. DEAL STRUCTURE - 2 Column Table */}
              <div className="bg-[#0e1424] border border-slate-750 rounded-3xl p-6 space-y-4 shadow-xl">
                <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                  <h3 className="text-xs font-black text-cyan-400 uppercase tracking-widest">DEAL STRUCTURE</h3>
                  <span className="text-[10px] text-slate-400 font-bold">REAL-TIME CALCULATION</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-xs">
                  <div className="space-y-2.5">
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Sale price</span>
                      <span className="font-bold text-white">${salePrice.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Accessories</span>
                      <span className="font-bold text-white">${accessories.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Doc fee</span>
                      <span className="font-bold text-white">${docFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">MV registration</span>
                      <span className="font-bold text-white">${registrationFee.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Sales tax ({(taxRate * 100).toFixed(1)}%)</span>
                      <span className="font-bold text-white">${salesTax.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t border-slate-700 text-cyan-400 font-extrabold text-sm">
                      <span>Total amount due</span>
                      <span>${totalAmountDue.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="space-y-2.5">
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Down payment</span>
                      <span className="font-bold text-emerald-400">-${downPayment.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Rebates</span>
                      <span className="font-bold text-emerald-400">-${customerRebate.toLocaleString()}</span>
                    </div>
                    {hasTrade && (
                      <div className="flex justify-between py-1 border-b border-slate-800">
                        <span className="text-slate-400">Trade equity</span>
                        <span className={`font-bold ${tradeEquity >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                          {tradeEquity >= 0 ? `-$${tradeEquity.toLocaleString()}` : `+$${Math.abs(tradeEquity).toLocaleString()}`}
                        </span>
                      </div>
                    )}
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Base amount financed</span>
                      <span className="font-bold text-cyan-400">${baseAmountFinanced.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-1 border-b border-slate-800">
                      <span className="text-slate-400">Protection plans add</span>
                      <span className="font-bold text-cyan-300">+${productTotal.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between py-2 border-t border-slate-700 text-cyan-400 font-extrabold text-sm">
                      <span>Total amount financed</span>
                      <span>${totalAmountFinanced.toLocaleString()}</span>
                    </div>
                  </div>
                </div>

                {/* Lender & Credit Meta Footer */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-800 text-[11px]">
                  <div>
                    <span className="text-slate-500 uppercase font-bold text-[9px] block">LENDER</span>
                    <span className="font-bold text-slate-200">{activePlan.lenderName || activePlan.name}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 uppercase font-bold text-[9px] block">CREDIT TIER</span>
                    <span className="font-bold text-slate-200">{creditTier}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 uppercase font-bold text-[9px] block">TOTAL OF PAYMENTS</span>
                    <span className="font-bold text-slate-200">${Math.round(monthlyFinancePayment * termMonths).toLocaleString()}</span>
                  </div>
                  <div>
                    <span className="text-slate-500 uppercase font-bold text-[9px] block">TOTAL INTEREST</span>
                    <span className="font-bold text-slate-200">${Math.max(0, Math.round((monthlyFinancePayment * termMonths) - totalAmountFinanced)).toLocaleString()}</span>
                  </div>
                </div>
              </div>

              {/* 2. TUNE IT LIVE - Interactive Term, Down Payment & Lender */}
              <div className="bg-[#0e1424] border border-slate-750 rounded-3xl p-6 space-y-5 shadow-xl">
                <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                  <h3 className="text-xs font-black text-cyan-400 uppercase tracking-widest">TUNE IT LIVE</h3>
                  <span className="text-[10px] text-emerald-400 font-bold bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/30">INSTANT SYNC</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
                  <div>
                    <span className="text-[10px] font-bold text-slate-400 uppercase mb-2 block">Loan Term Duration</span>
                    <div className="flex gap-2">
                      {[48, 60, 66, 72, 84].map(t => (
                        <button
                          key={t}
                          onClick={() => setSelectedTerm(t)}
                          className={`w-12 h-10 rounded-xl font-black text-xs transition-all cursor-pointer ${selectedTerm === t
                            ? 'bg-cyan-400 text-slate-950 font-black shadow-md scale-105'
                            : 'bg-[#141b2e] border border-slate-700 text-slate-300 hover:border-slate-500'
                            }`}
                        >
                          {t}m
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="flex justify-between items-baseline mb-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase">Cash down payment</span>
                      <span className="text-xs font-black text-cyan-400">${downPayment.toLocaleString()}</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="30000"
                      step="250"
                      value={customDownPayment}
                      onChange={(e) => setCustomDownPayment(Number(e.target.value))}
                      className="w-full h-2.5 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                    />
                  </div>
                </div>

                {/* Lender on the Desk Selectors */}
                <div className="space-y-2 pt-2 border-t border-slate-800">
                  <span className="text-[10px] font-bold text-slate-400 uppercase block">Selected Partner Lender</span>
                  <div className="flex flex-wrap gap-2">
                    {lendersList.map(l => {
                      const isSel = (l._id || l.lender_id) === selectedLenderId;
                      return (
                        <button
                          key={l._id || l.lender_id}
                          onClick={() => setSelectedLenderId(l._id || l.lender_id)}
                          className={`px-3.5 py-2 rounded-xl text-xs font-bold border transition-all cursor-pointer ${isSel
                            ? 'bg-cyan-500/20 text-cyan-300 border-cyan-400 shadow-sm'
                            : 'bg-[#10172c] border-slate-750 text-slate-400 hover:text-white'
                            }`}
                        >
                          {l.name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* 3. PROTECTION MENU ACCORDION */}
              <div className="bg-[#0e1424] border border-slate-750 rounded-3xl p-6 space-y-4 shadow-xl">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                  <div>
                    <h3 className="text-xs font-black text-cyan-400 uppercase tracking-widest">PROTECTION MENU</h3>
                    <p className="text-[11px] text-slate-300">Customized coverage options &middot; All active by default</p>
                  </div>
                  <span className="text-xs font-bold text-cyan-400 bg-cyan-500/10 border border-cyan-500/30 px-2.5 py-1 rounded-lg">
                    +${productTotal.toLocaleString()} ({selectedProducts.length} plans)
                  </span>
                </div>

                {/* Accordion Slots */}
                <div className="space-y-3 pt-1">
                  {fandiList.map((prod) => {
                    const prodId = prod.product_id || prod._id || prod.productId;
                    const isSelected = selectedFAndIIds.includes(prodId);
                    const isExpanded = expandedFandiSlot === prodId;
                    const monthlyCost = calcPmt(prod.price, apr, termMonths);

                    return (
                      <div
                        key={prodId}
                        className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isSelected
                          ? 'bg-gradient-to-br from-[#0e172a] via-[#101b33] to-[#0c1324] border-cyan-500/40 ring-1 ring-cyan-500/30 shadow-md'
                          : 'bg-[#0a0f1e] border-slate-800 opacity-80'
                          }`}
                      >
                        <div
                          className="p-3.5 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors"
                          onClick={() => setExpandedFandiSlot(isExpanded ? null : prodId)}
                        >
                          <div className="flex items-center gap-3">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleToggleFAndI(prod);
                              }}
                              className={`w-9 h-9 rounded-full flex items-center justify-center font-black text-xs shadow-md transition-all cursor-pointer ${isSelected
                                ? 'bg-gradient-to-tr from-cyan-400 via-teal-300 to-emerald-400 text-slate-950 scale-105'
                                : 'bg-[#141b2e] border border-slate-700 text-slate-400 hover:text-white hover:border-cyan-400'
                                }`}
                              title={isSelected ? "Remove Protection" : "Add Coverage Back"}
                            >
                              {isSelected ? '✓' : '+'}
                            </button>
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-xs font-bold text-white">{prod.name}</h4>
                                {prod.isBasePlan && (
                                  <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[8px] font-black uppercase">BASE</span>
                                )}
                              </div>
                              <span className="text-[10px] text-slate-400">{prod.term || "7 yr / 100,000 mi"} &middot; ${Number(prod.price).toLocaleString()}</span>
                            </div>
                          </div>

                          <div className="flex items-center gap-2.5">
                            <span className="text-xs font-black text-cyan-400">+${monthlyCost.toFixed(2)}/mo</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setViewingFAndIPlan(prod);
                              }}
                              className="text-slate-400 hover:text-cyan-300 p-1 cursor-pointer"
                              title="View Plan Details"
                            >
                              <FaInfoCircle size={12} />
                            </button>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedFandiSlot(isExpanded ? null : prodId);
                              }}
                              className="text-slate-400 hover:text-white p-1 cursor-pointer"
                            >
                              {isExpanded ? <FaChevronUp size={11} /> : <FaChevronDown size={11} />}
                            </button>
                          </div>
                        </div>

                        {isExpanded && (
                          <div className="px-4 pb-3.5 pt-1 text-xs border-t border-slate-800/80 space-y-2 animate-fadeIn bg-[#080d1a]/60">
                            <p className="text-slate-300 leading-relaxed text-[11px] pt-1">{prod.description}</p>

                            {isSelected ? (
                              <div className="space-y-1.5 pt-1">
                                <span className="text-[9px] font-black text-emerald-400 uppercase tracking-wider block">INCLUDED BENEFITS</span>
                                {(prod.benefits || [
                                  "100% parts & labor coverage nationwide",
                                  "Engine, transmission, drive axle protection",
                                  "24/7 roadside assistance & towing"
                                ]).map((b, bi) => (
                                  <div key={bi} className="flex items-center gap-1.5 text-slate-300 text-[10px]">
                                    <span className="text-emerald-400 font-bold">✓</span>
                                    <span>{b}</span>
                                  </div>
                                ))}

                                <div className="pt-2 flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleFAndI(prod)}
                                    className="text-[10px] text-rose-400 hover:text-rose-300 font-bold flex items-center gap-1 cursor-pointer hover:underline"
                                  >
                                    ✕ Remove Protection (-${monthlyCost.toFixed(2)}/mo)
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="space-y-2 pt-1">
                                <div className="bg-rose-950/20 border border-rose-500/20 p-2.5 rounded-xl space-y-1 text-rose-200/90 text-[10px]">
                                  <span className="font-black text-rose-400 uppercase block">⚠️ Unprotected Risk</span>
                                  <p>{prod.warningMsg || "Declining exposes you to out-of-pocket repair costs averaging $1,800–$4,500."}</p>
                                </div>

                                <div className="flex justify-end">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleFAndI(prod)}
                                    className="px-3 py-1 rounded-xl bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-400 text-[10px] font-bold flex items-center gap-1 cursor-pointer shadow-sm transition-all"
                                  >
                                    + Add Coverage Back (+${monthlyCost.toFixed(2)}/mo)
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 4. SALES CONSULTANT ASSIGNMENT */}
              {(() => {
                const isAssigned = salespersonSubmitted || !!salespersonId || !!deal?.salespersonId;
                const assignedName = salespersonId || deal?.salespersonId || "Sales Consultant";

                return (
                  <div className={`rounded-3xl p-6 shadow-xl transition-all space-y-4 border ${
                    !isAssigned
                      ? 'bg-gradient-to-r from-[#1c1812] via-[#161f36] to-[#141b2d] border-amber-500/60 ring-2 ring-amber-500/20'
                      : 'bg-gradient-to-r from-[#121a30] via-[#0e1424] to-[#121a30] border-cyan-500/30'
                  }`}>
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FaIdBadge className={!isAssigned ? "text-amber-400" : "text-cyan-400"} size={15} />
                          <span className={`text-[10px] font-black uppercase tracking-widest ${!isAssigned ? "text-amber-400" : "text-cyan-400"}`}>
                            SALES CONSULTANT ASSIGNMENT {!isAssigned && "&middot; REQUIRED"}
                          </span>
                        </div>
                        <h3 className="text-base font-black text-white">
                          {isAssigned ? `Assigned to ${assignedName}` : "Assign Deal to Your Sales Consultant"}
                        </h3>
                        <p className="text-xs text-slate-300">
                          {isAssigned
                            ? "This deal proposal and customer handoff session are bound to your workstation."
                            : "Enter or select a sales consultant below to generate the customer handoff QR code & mobile companion link."}
                        </p>
                      </div>

                      {!isAssigned ? (
                        <div className="w-full sm:w-80 space-y-2">
                          <RoundedPillInput
                            value={salespersonId}
                            onChange={setSalespersonId}
                            onSubmit={() => handleSalespersonSubmit()}
                            placeholder="Enter Salesperson ID / Name"
                          />
                        </div>
                      ) : (
                        <div className="flex items-center gap-3">
                          <div className="bg-emerald-500/10 border border-emerald-500/30 px-5 py-3 rounded-2xl flex items-center gap-3">
                            <FaCheckCircle className="text-emerald-400" size={20} />
                            <div>
                              <h4 className="text-xs font-black text-emerald-400">Deal Assigned: {assignedName}</h4>
                              <p className="text-[9px] text-slate-300">Active on desk &middot; QR unlocked</p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSalespersonSubmitted(false);
                              setSalespersonId("");
                            }}
                            className="text-[10px] text-slate-400 hover:text-white underline cursor-pointer p-1"
                          >
                            Reassign
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Quick 1-Click Assignment Buttons if unassigned */}
                    {!isAssigned && (
                      <div className="pt-2 border-t border-amber-500/30 flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-bold text-amber-300">Quick Select:</span>
                        {[
                          { name: "Bobby Bonser", id: "SP-101" },
                          { name: "Sarah Jenkins", id: "SP-102" },
                          { name: "Alex Rivera", id: "SP-103" }
                        ].map((sp) => (
                          <button
                            key={sp.id}
                            type="button"
                            onClick={() => handleSalespersonSubmit(`${sp.name} (${sp.id})`)}
                            className="px-3 py-1 rounded-xl bg-[#18243f] hover:bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 text-[10px] font-bold cursor-pointer transition-colors"
                          >
                            + {sp.name} ({sp.id})
                          </button>
                        ))}
                        <button
                          type="button"
                          onClick={() => handleSalespersonSubmit("Desk Consultant (SP-100)")}
                          className="px-3 py-1 rounded-xl bg-cyan-400 text-slate-950 text-[10px] font-black uppercase tracking-wider cursor-pointer hover:bg-cyan-300"
                        >
                          ⚡ Auto-Assign Desk
                        </button>
                      </div>
                    )}
                  </div>
                );
              })()}

            </div>

            {/* RIGHT COLUMN: F&I Connect Digital Handoff, Live OCR, Digital Signature & Final Approval (Span 6) */}
            <div className="lg:col-span-6 space-y-6">

              {/* 1. F&I CONNECT: LIVE CUSTOMER PHONE CONNECTION & SYNC HUB (GATED) */}
              {(() => {
                const isAssigned = salespersonSubmitted || !!salespersonId || !!deal?.salespersonId;
                const assignedName = salespersonId || deal?.salespersonId || "Sales Consultant";
                const companionUrl = `${window.location.origin}/kiosk/mobile/${token || routeToken || 'deal_live'}`;

                return (
                  <div className={`rounded-3xl p-6 shadow-2xl transition-all space-y-5 border-2 ${
                    !isAssigned
                      ? 'bg-[#090d18] border-slate-800 opacity-90'
                      : 'bg-gradient-to-br from-[#0b1326] via-[#101a33] to-[#080d1a] border-cyan-500/40 shadow-[0_0_40px_rgba(6,182,212,0.18)]'
                  }`}>
                    
                    {/* Header with Live Status Indicator */}
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-cyan-500/20 pb-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <FaMobileAlt className={isAssigned ? "text-cyan-400" : "text-slate-500"} size={16} />
                          <span className={`text-[10px] font-black uppercase tracking-widest ${isAssigned ? "text-cyan-400" : "text-slate-400"}`}>
                            F&I CONNECT &middot; {isAssigned ? "DIGITAL HANDOFF ACTIVE" : "LOCKED"}
                          </span>
                        </div>
                        <h3 className="text-base font-black text-white">
                          {customerName || "Customer"} &middot; Deal #{deal?.dealNumber || "PHS-00124"}
                        </h3>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setShowPrintReportModal(true)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-slate-800 hover:bg-slate-700 text-cyan-300 border border-slate-700 shadow-sm transition-all cursor-pointer"
                        >
                          <FaPrint size={11} /> <span>Official Deal Sheet</span>
                        </button>
                        {isAssigned ? (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 shadow-sm">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                            <span>{deal?.customerSession?.connected ? "🟢 Customer Connected" : `🟢 Live Sync &middot; ${assignedName}`}</span>
                          </span>
                        ) : (
                          <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-amber-500/15 border border-amber-500/40 text-amber-400">
                            <FaLock size={10} />
                            <span>🔒 Assign Consultant to Unlock</span>
                          </span>
                        )}
                      </div>
                    </div>

                    {/* QR Code & Phone Handoff Section */}
                    <div className="grid grid-cols-1 sm:grid-cols-12 gap-5 items-center bg-[#070c18] border border-cyan-500/25 p-4 rounded-2xl relative overflow-hidden">
                      
                      {/* Locked Overlay if not assigned */}
                      {!isAssigned && (
                        <div className="absolute inset-0 bg-[#060a14]/90 backdrop-blur-sm z-20 flex flex-col items-center justify-center p-4 text-center space-y-2">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 border border-amber-400 text-amber-400 flex items-center justify-center text-base">
                            <FaLock />
                          </div>
                          <h4 className="text-xs font-black text-white">QR Code &amp; Link Locked</h4>
                          <p className="text-[11px] text-amber-200/90 max-w-xs leading-snug">
                            Please assign a Sales Consultant on the left to generate the customer's secure QR code.
                          </p>
                          <button
                            type="button"
                            onClick={() => handleSalespersonSubmit("Desk Consultant (SP-100)")}
                            className="bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 font-black text-[10px] uppercase px-3.5 py-1.5 rounded-xl cursor-pointer shadow-md"
                          >
                            ⚡ Auto-Assign Consultant Now
                          </button>
                        </div>
                      )}

                      <div className="sm:col-span-4 flex flex-col items-center justify-center p-2 bg-white rounded-2xl shadow-xl border-2 border-cyan-400 flex-shrink-0">
                        <img
                          src={`https://api.qrserver.com/v1/create-qr-code/?size=250x250&data=${encodeURIComponent(companionUrl)}`}
                          alt="Scan to continue on phone"
                          className="w-28 h-28 object-contain"
                        />
                        <span className="text-[9px] font-black text-slate-900 uppercase tracking-wider mt-1">Scan with Phone</span>
                      </div>

                      <div className="sm:col-span-8 space-y-2 text-left">
                        <div className="flex items-center gap-2 text-cyan-300 text-xs font-bold">
                          <FaLock size={12} className="text-cyan-400" />
                          <span>Private 256-Bit Companion Session</span>
                        </div>
                        <p className="text-xs text-slate-300 leading-relaxed">
                          Customer completes sensitive information (SSN, income) and driver's license scans directly on their phone.
                        </p>
                        <div className="flex flex-wrap items-center gap-2 pt-1">
                          <button
                            type="button"
                            onClick={() => setShowPhoneSimulator(true)}
                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 font-black text-xs uppercase tracking-wider shadow-md hover:scale-105 transition-transform cursor-pointer"
                          >
                            <FaMobileAlt size={12} /> Test Phone Companion &rarr;
                          </button>
                          <a
                            href={companionUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-[11px] font-bold text-cyan-400 hover:text-cyan-300 underline cursor-pointer"
                          >
                            <FaExternalLinkAlt size={10} /> Open in New Tab
                          </a>
                        </div>
                        <span className="text-[10px] text-slate-400 block pt-0.5">
                          Session expires in {Math.floor(companionSessionTime / 60)}:{(companionSessionTime % 60).toString().padStart(2, '0')} min
                        </span>
                      </div>
                    </div>

                    {/* Customer Progress & Verified Details Bar */}
                    <div className="bg-[#0e1628] border border-slate-750 p-4 rounded-2xl space-y-3 text-xs">
                      <div className="flex justify-between items-center border-b border-slate-800 pb-2">
                        <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest">
                          CUSTOMER MOBILE LIVE PROGRESS
                        </span>
                        <span className="text-xs font-black text-emerald-400">
                          {deal?.customerSession?.progressPercent || (Object.keys(uploadedDocs).length > 0 ? 80 : 40)}% COMPLETE
                        </span>
                      </div>

                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-[11px]">
                        <div className="bg-[#121b30] p-2.5 rounded-xl border border-slate-750">
                          <span className="text-[9px] text-slate-400 font-bold block uppercase">Personal Info</span>
                          <span className="text-emerald-400 font-bold flex items-center gap-1 mt-0.5">
                            <FaCheck size={9} /> Confirmed
                          </span>
                        </div>
                        <div className="bg-[#121b30] p-2.5 rounded-xl border border-slate-750">
                          <span className="text-[9px] text-slate-400 font-bold block uppercase">Private Credit</span>
                          <span className="text-emerald-400 font-bold flex items-center gap-1 mt-0.5">
                            <FaLock size={9} /> {deal?.customer?.ssnMasked || "•••• 1234"}
                          </span>
                        </div>
                        <div className="bg-[#121b30] p-2.5 rounded-xl border border-slate-750">
                          <span className="text-[9px] text-slate-400 font-bold block uppercase">Employment</span>
                          <span className="text-white font-bold truncate block mt-0.5">
                            {deal?.customer?.employer || "Pohanka Group"}
                          </span>
                        </div>
                        <div className="bg-[#121b30] p-2.5 rounded-xl border border-slate-750">
                          <span className="text-[9px] text-slate-400 font-bold block uppercase">Documents</span>
                          <span className="text-cyan-400 font-bold mt-0.5 block">
                            {Object.keys(uploadedDocs).length} Received
                          </span>
                        </div>
                      </div>
                    </div>

                  </div>
                );
              })()}

              {/* 2. AI DOCUMENT ASSISTANT, OCR REVIEW & SALESPERSON APPROVAL PANEL */}
              <div className="bg-[#0e1424] border border-slate-750 rounded-3xl p-6 space-y-5 shadow-xl">
                <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <FaRobot className="text-cyan-400" size={14} />
                      <h3 className="text-xs font-black uppercase tracking-widest text-cyan-400">
                        AI DOCUMENT ASSISTANT & REVIEW
                      </h3>
                    </div>
                    <p className="text-[11px] text-slate-300 mt-0.5">
                      Live OCR extraction, S3 storage & salesperson contract verification
                    </p>
                  </div>
                  <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-xl">
                    {Object.keys(uploadedDocs).length}/5 Documents
                  </span>
                </div>

                {/* Customer Signature Card if Signed */}
                {deal?.signature?.dataUrl && (
                  <div className="bg-gradient-to-r from-[#0c1a2f] via-[#0d1e38] to-[#0a1526] border-2 border-emerald-500/50 p-4 rounded-2xl space-y-2 shadow-lg animate-fadeIn">
                    <div className="flex justify-between items-center text-xs">
                      <span className="text-[10px] font-black text-emerald-400 uppercase tracking-widest flex items-center gap-1.5">
                        <FaCheckCircle /> CUSTOMER DIGITAL SIGNATURE VERIFIED
                      </span>
                      <span className="text-[10px] text-slate-400">
                        Signed at {deal?.signature?.signedAt ? new Date(deal.signature.signedAt).toLocaleTimeString() : "Just now"}
                      </span>
                    </div>

                    <div className="flex items-center justify-between bg-[#080d1a] p-3 rounded-xl border border-slate-800">
                      <div className="h-14 bg-white/95 rounded-lg p-1.5 flex items-center justify-center max-w-[200px] border border-slate-700 overflow-hidden shadow-sm">
                        <img
                          src={deal.signature.dataUrl}
                          alt="Customer Signature"
                          className="max-h-full max-w-full object-contain"
                        />
                      </div>
                      <div className="text-right space-y-0.5">
                        <span className="text-[9px] text-slate-400 uppercase font-bold block">
                          {deal?.signature?.signatureMethod === "UPLOAD" 
                            ? "📸 Uploaded Photo Signature" 
                            : deal?.signature?.signatureMethod === "TYPE" 
                              ? "⌨️ Digital Typed Signature" 
                              : "✍️ Touch/Stylus Signature"}
                        </span>
                        <strong className="text-xs text-white block">{deal?.signature?.signedBy || customerName || "John Smith"}</strong>
                      </div>
                    </div>
                  </div>
                )}

                {/* Received Documents Inspection Cards */}
                {Object.keys(uploadedDocs).length === 0 ? (
                  <div className="p-6 border border-dashed border-slate-750 rounded-2xl text-center space-y-2 bg-[#090e1c]">
                    <div className="w-10 h-10 rounded-full bg-cyan-500/10 text-cyan-400 flex items-center justify-center mx-auto">
                      <FaUpload size={16} />
                    </div>
                    <h4 className="text-xs font-bold text-white">Waiting for customer mobile upload</h4>
                    <p className="text-[11px] text-slate-400 max-w-sm mx-auto">
                      Customer is scanning their driver's license and income proof on their phone. Documents will stream in live.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3.5 pt-1">
                    {Object.entries(uploadedDocs).map(([slotKey, docInfo]) => {
                      const isAccepted = docInfo.status === "ACCEPTED";
                      const isRescanRequested = docInfo.status === "REQUEST_NEW_SCAN";
                      const checks = docInfo.aiChecks || {};
                      const ocrFields = checks.fields || {};

                      return (
                        <div
                          key={slotKey}
                          className={`rounded-2xl border transition-all p-4 space-y-3 ${
                            isAccepted
                              ? 'bg-emerald-950/20 border-emerald-500/50 shadow-md'
                              : isRescanRequested
                                ? 'bg-amber-950/20 border-amber-500/50'
                                : 'bg-[#12192e] border-cyan-500/30'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3">
                              {docInfo.previewUrl && (
                                <div
                                  onClick={() => setSelectedDocPreview({ title: slotKey, ...docInfo })}
                                  className="w-14 h-12 rounded-xl bg-slate-900 border border-slate-700 overflow-hidden flex-shrink-0 cursor-pointer relative group"
                                >
                                  <img
                                    src={docInfo.previewUrl || docInfo.s3Url}
                                    alt="Doc preview"
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform"
                                  />
                                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center text-white text-xs transition-opacity">
                                    <FaSearchPlus />
                                  </div>
                                </div>
                              )}
                              <div className="space-y-0.5">
                                <div className="flex items-center gap-2">
                                  <h4 className="text-xs font-black text-white">{slotKey}</h4>
                                  <span className={`text-[9px] font-black uppercase px-2 py-0.5 rounded-full border ${
                                    isAccepted
                                      ? 'text-emerald-300 bg-emerald-500/20 border-emerald-500/40'
                                      : isRescanRequested
                                        ? 'text-amber-300 bg-amber-500/20 border-amber-500/40'
                                        : 'text-cyan-300 bg-cyan-500/20 border-cyan-500/40'
                                  }`}>
                                    {isAccepted ? "✓ Accepted" : isRescanRequested ? "⚠️ Rescan Requested" : "🟡 Under Review"}
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-400">
                                  Uploaded at {docInfo.uploadedAt || "Just now"} &middot; {docInfo.fileName || "scan.jpg"}
                                </p>
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => {
                                  setEditingOcrSlotDesk(slotKey);
                                  setEditOcrFieldsDesk({ ...(checks.fields || {}) });
                                }}
                                className="text-cyan-400 hover:text-white bg-cyan-500/15 hover:bg-cyan-500/30 px-2 py-1 rounded-lg text-[10px] font-bold flex items-center gap-1 cursor-pointer transition-colors border border-cyan-500/30"
                                title="Edit / Review Extracted Fields"
                              >
                                <FaEdit size={10} /> Edit OCR Data
                              </button>
                              <button
                                type="button"
                                onClick={() => setSelectedDocPreview({ title: slotKey, ...docInfo })}
                                className="text-slate-400 hover:text-cyan-300 text-xs p-1"
                                title="Zoom Preview"
                              >
                                <FaSearchPlus size={13} />
                              </button>
                            </div>
                          </div>

                          {/* AI Checks Metadata Box */}
                          <div className="bg-[#0a0f1d] p-3 rounded-xl border border-slate-800 space-y-1.5 text-[11px]">
                            <div className="flex justify-between items-center">
                              <span className="text-[9px] font-black text-cyan-400 uppercase flex items-center gap-1">
                                <FaCheckCircle className="text-emerald-400" /> AI OCR ANALYSIS
                              </span>
                              <span className="text-[9px] font-bold text-slate-400">Match Confidence {checks.confidenceScore || "99.1%"}</span>
                            </div>

                            <div className="grid grid-cols-2 gap-1.5 text-slate-300 pt-1">
                              {Object.entries(ocrFields).map(([k, v]) => (
                                <div key={k} className="truncate bg-[#070b16] p-1.5 rounded-lg border border-slate-800/80">
                                  <span className="text-slate-500 capitalize text-[8px] font-bold block truncate">{k.replace(/([A-Z])/g, ' $1')}</span>
                                  <strong className="text-white text-[10px] truncate block">{String(v)}</strong>
                                </div>
                              ))}
                            </div>

                            {/* Potential Issue Flag */}
                            {checks.issues && checks.issues.length > 0 && (
                              <div className="mt-1 pt-1.5 border-t border-slate-800/80 flex items-start gap-1.5 text-amber-300 text-[10px]">
                                <FaExclamationTriangle className="flex-shrink-0 mt-0.5" size={10} />
                                <span>Note: {checks.issues[0]}</span>
                              </div>
                            )}
                          </div>

                          {/* Manager Review Action Buttons */}
                          <div className="flex justify-end gap-2 pt-1">
                            {!isAccepted && (
                              <button
                                type="button"
                                onClick={() => handleManagerDocAction(slotKey, "ACCEPTED", "Verified by F&I Manager")}
                                className="px-3.5 py-1.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-[11px] uppercase tracking-wider transition-all cursor-pointer shadow-md flex items-center gap-1.5"
                              >
                                <FaCheck size={11} /> Accept Document
                              </button>
                            )}

                            {!isRescanRequested && (
                              <button
                                type="button"
                                onClick={() => handleManagerDocAction(slotKey, "REQUEST_NEW_SCAN", "Please upload a clearer image of this document")}
                                className="px-3 py-1.5 rounded-xl bg-[#1a233a] hover:bg-rose-500/20 text-rose-300 hover:text-rose-200 border border-rose-500/30 font-bold text-[11px] transition-colors cursor-pointer flex items-center gap-1"
                              >
                                ✕ Request New Scan
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Salesperson Review & Approve Action */}
                <div className="pt-3 border-t border-slate-800 space-y-3">
                  {deal?.status === "APPROVED" || deal?.approvalDetails?.approved ? (
                    <div className="bg-gradient-to-r from-emerald-950/40 via-emerald-900/30 to-[#0c182c] border-2 border-emerald-500 p-4 rounded-2xl text-center space-y-2 shadow-2xl animate-scaleIn">
                      <div className="flex items-center justify-center gap-2 text-emerald-400 font-black text-sm uppercase tracking-wider">
                        <FaCheckCircle size={18} /> DEAL COMPLETED &amp; APPROVED
                      </div>
                      <p className="text-xs text-slate-200">
                        Approved by <strong>{deal?.approvalDetails?.approvedBy || salespersonId || "Sales Consultant"}</strong> &middot; Ready for vehicle delivery.
                      </p>
                      <button
                        type="button"
                        onClick={() => setShowPrintReportModal(true)}
                        className="mt-1 px-4 py-2.5 bg-emerald-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl shadow-lg hover:bg-emerald-300 transition-colors cursor-pointer flex items-center justify-center gap-2 mx-auto"
                      >
                        <FaPrint size={14} /> Print / Export Official Buyer's Order (PDF)
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <button
                        type="button"
                        onClick={handleApproveAndCompleteDeal}
                        className="w-full bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-slate-950 font-black py-3.5 rounded-2xl text-xs uppercase tracking-wider shadow-2xl hover:scale-[1.01] active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                      >
                        <FaCheckCircle size={14} /> Review &amp; Approve Deal (Make Deal Complete)
                      </button>
                      <p className="text-[10px] text-slate-400 text-center">
                        Salesperson verifies that all OCR extracted fields, S3 uploads, and customer digital signatures are cross-checked.
                      </p>
                    </div>
                  )}
                </div>

                {/* Quick Add / Direct Desk Upload Slots */}
                <div className="pt-3 border-t border-slate-800/80 space-y-2.5">
                  <div className="flex justify-between items-center">
                    <span className="text-[10px] font-black text-cyan-400 uppercase tracking-wider block">
                      DESK DIRECT UPLOAD &amp; OCR SCANNING
                    </span>
                    <span className="text-[9px] text-slate-400 font-bold">Snap or Select File</span>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {[
                      { title: "Driver's License (Front)", icon: <FaIdCard size={12} /> },
                      { title: "Driver's License (Back)", icon: <FaIdCard size={12} /> },
                      { title: "Proof of Income", icon: <FaFileInvoiceDollar size={12} /> },
                      { title: "Insurance Card", icon: <FaShieldAlt size={12} /> },
                      { title: "Proof of Residence", icon: <FaBuilding size={12} /> }
                    ].map(({ title, icon }) => (
                      <label
                        key={title}
                        className={`p-2.5 rounded-xl text-[10px] font-bold border transition-all cursor-pointer flex items-center justify-between gap-2 select-none ${
                          uploadedDocs[title]
                            ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40 shadow-sm'
                            : 'bg-[#12192e] text-slate-300 border-slate-750 hover:text-white hover:border-cyan-500/40 hover:bg-[#18233f]'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span className={uploadedDocs[title] ? "text-emerald-400" : "text-cyan-400"}>
                            {uploadedDocs[title] ? <FaCheck size={10} /> : icon}
                          </span>
                          <span className="truncate">{uploadedDocs[title] ? `✓ ${title}` : `+ Upload ${title}`}</span>
                        </div>
                        {deskUploadingSlot === title ? (
                          <span className="w-3 h-3 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
                        ) : (
                          <span className="text-[9px] bg-cyan-500/20 text-cyan-300 px-1.5 py-0.5 rounded uppercase font-black flex-shrink-0">
                            {uploadedDocs[title] ? "Replace" : "Snap"}
                          </span>
                        )}
                        <input
                          type="file"
                          accept="image/*,application/pdf"
                          onChange={(e) => handleDeskDocUpload(title, e.target.files?.[0])}
                          className="hidden"
                        />
                      </label>
                    ))}
                  </div>
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* DOCUMENT ZOOM PREVIEW MODAL */}
        {selectedDocPreview && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-[#0e1628] border border-cyan-500/40 rounded-3xl max-w-2xl w-full p-6 space-y-4 shadow-2xl animate-scaleIn">
              <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                <div>
                  <h3 className="text-sm font-black text-white">{selectedDocPreview.title}</h3>
                  <span className="text-[10px] text-slate-400">Uploaded at {selectedDocPreview.uploadedAt || "Just now"}</span>
                </div>
                <button
                  onClick={() => setSelectedDocPreview(null)}
                  className="text-slate-400 hover:text-white p-1 text-lg cursor-pointer"
                >
                  ✕
                </button>
              </div>

              <div className="max-h-[60vh] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 flex items-center justify-center p-2">
                <img
                  src={selectedDocPreview.previewUrl || selectedDocPreview.s3Url}
                  alt="Zoom"
                  className="max-h-full max-w-full object-contain rounded-xl"
                />
              </div>

              <div className="flex flex-col sm:flex-row justify-between items-center gap-3 pt-2">
                <span className="text-xs text-emerald-400 font-bold flex items-center gap-1.5">
                  <FaCheckCircle /> High-Resolution AI Image OCR Validated
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      handleManagerDocAction(selectedDocPreview.title, "ACCEPTED", "Verified by F&I Manager");
                      setSelectedDocPreview(null);
                    }}
                    className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black text-xs uppercase cursor-pointer"
                  >
                    Accept Document
                  </button>
                  <button
                    onClick={() => {
                      handleManagerDocAction(selectedDocPreview.title, "REQUEST_NEW_SCAN", "Please provide a clearer photo");
                      setSelectedDocPreview(null);
                    }}
                    className="px-4 py-2 rounded-xl bg-rose-500/20 text-rose-300 border border-rose-500/40 font-bold text-xs cursor-pointer"
                  >
                    Request New Scan
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* DESK OCR FIELD INSPECTOR & EDITOR MODAL */}
        {editingOcrSlotDesk && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-[#0e1628] border-2 border-cyan-500/40 rounded-3xl p-6 max-w-lg w-full max-h-[85vh] flex flex-col shadow-2xl space-y-4">
              <div className="flex justify-between items-center border-b border-slate-800 pb-3 flex-shrink-0">
                <div>
                  <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest block">DESK OCR FIELD INSPECTOR</span>
                  <h3 className="text-sm font-black text-white">{editingOcrSlotDesk}</h3>
                </div>
                <button
                  onClick={() => setEditingOcrSlotDesk(null)}
                  className="text-slate-400 hover:text-white text-xs cursor-pointer p-1"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto space-y-3 pr-1 text-xs scrollbar-thin">
                <p className="text-[11px] text-slate-300">
                  Review or modify the OCR extracted fields for verified deal documentation:
                </p>
                {Object.entries(editOcrFieldsDesk).map(([key, val]) => (
                  <div key={key} className="space-y-1">
                    <label className="text-[10px] font-bold text-slate-400 capitalize block">
                      {key.replace(/([A-Z])/g, ' $1')}
                    </label>
                    <input
                      type="text"
                      value={val}
                      onChange={(e) => setEditOcrFieldsDesk({ ...editOcrFieldsDesk, [key]: e.target.value })}
                      className="w-full bg-[#060a14] border border-slate-750 focus:border-cyan-400 rounded-xl px-3 py-2 text-xs text-white outline-none"
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-2.5 pt-2 border-t border-slate-800 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => setEditingOcrSlotDesk(null)}
                  className="w-1/3 bg-[#141d33] text-slate-300 font-bold py-2.5 rounded-xl text-xs hover:bg-[#1a2642] cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleSaveOcrFieldsDesk(editingOcrSlotDesk, editOcrFieldsDesk)}
                  className="flex-1 bg-gradient-to-r from-cyan-400 to-blue-500 text-slate-950 font-black py-2.5 rounded-xl text-xs uppercase tracking-wider hover:brightness-110 shadow-lg cursor-pointer"
                >
                  Save &amp; Apply Changes &rarr;
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CUSTOMER PHONE SIMULATOR MODAL (SIDE-BY-SIDE INTERACTIVE DEMO) */}
        {showPhoneSimulator && (
          <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-[#0b1222] border-2 border-cyan-400 rounded-[40px] max-w-md w-full h-[88vh] shadow-[0_0_50px_rgba(6,182,212,0.3)] flex flex-col overflow-hidden animate-scaleIn relative">
              {/* Phone Notch Bar */}
              <div className="bg-[#080d1a] px-6 py-3 flex items-center justify-between border-b border-slate-800 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-[11px] font-black text-cyan-400 tracking-wider">F&I CONNECT &middot; PHONE COMPANION</span>
                </div>
                <button
                  onClick={() => setShowPhoneSimulator(false)}
                  className="text-slate-400 hover:text-white text-xs cursor-pointer font-bold px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-slate-700"
                >
                  ✕ Close
                </button>
              </div>

              {/* Live Iframe Embedded Mobile Companion */}
              <div className="flex-1 w-full h-full bg-[#060a14] overflow-hidden">
                <iframe
                  src={`/kiosk/mobile/${token || routeToken || 'deal_live'}`}
                  title="Customer Phone Companion"
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        )}

        {/* BOTTOM CHATBAR IN SUMMARY */}
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#080c18]/95 backdrop-blur-xl border-t border-slate-800 px-6 py-4">
          <div className="max-w-7xl mx-auto flex flex-col gap-2">
            {aiSpeechResponse && (
              <div className="bg-[#12192e] border border-cyan-500/40 rounded-2xl p-3 flex items-center justify-between gap-3 text-xs text-slate-100 shadow-lg animate-fadeInUp">
                <span className="flex items-center gap-2">
                  <FaRobot className="text-cyan-400" />
                  <span>{aiSpeechResponse}</span>
                </span>
                <button onClick={() => setAiSpeechResponse(null)} className="text-slate-400 hover:text-white text-xs cursor-pointer">✕</button>
              </div>
            )}
            <form onSubmit={handleConversationalSubmit} className="flex items-center gap-3">
              <input
                type="text"
                value={conversationalInput}
                onChange={(e) => setConversationalInput(e.target.value)}
                placeholder="Ask AIMEE questions, or enter coupon codes (e.g. 'BONUS1000')..."
                className="flex-1 bg-[#12192e] border border-slate-700/80 hover:border-slate-600 focus:border-cyan-400 rounded-2xl py-3 px-5 text-xs text-white outline-none"
              />
              <button
                type="submit"
                className="bg-cyan-400 text-slate-950 font-bold px-5 py-3 rounded-2xl flex items-center gap-2 text-xs uppercase cursor-pointer hover:bg-cyan-300 transition-colors"
              >
                <FaPaperPlane size={11} /> Send
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER INTERACTIVE 12-STEP QUESTIONNAIRE
  // -------------------------------------------------------------
  const renderCurrentQuestionContent = () => {
    switch (activeQuestionStep) {
      case 1: // Customer Name (Styled with Image 3 Input Box)
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`Welcome to ${currentDealershipTitle}. What is your name?`}
              subtitle="Enter your name to personalize your vehicle proposal."
              onComplete={() => setOptionsVisible(true)}
            />
            {optionsVisible && (
              <div className="pt-2 animate-fadeInUp">
                <RoundedPillInput
                  value={customerName}
                  onChange={setCustomerName}
                  placeholder="Enter your full name (e.g. John Doe)"
                  onSubmit={() => {
                    if (customerName.trim()) {
                      updateDealState({ customerInfo: { name: customerName } });
                      setActiveQuestionStep(2);
                    }
                  }}
                />
              </div>
            )}
          </div>
        );

      case 2: // Colors & Trim - SWATCHES ONLY (Matching Image 1 & 2 - Instant Click Advance)
        return (
          <div className="space-y-6 max-w-2xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`${customerName || 'Great'}, which exterior color would you like on the ${activeCar.year || ''} ${activeCar.make || ''} ${activeCar.model || 'Vehicle'}?`}
              subtitle="Select an exterior finish to instantly proceed."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-4 pt-2 animate-fadeInUp">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                  {(activeCar.colors && activeCar.colors.length > 0 ? activeCar.colors : [
                    { name: activeCar.color || "Standard Finish", hex: "#1e3a8a" }
                  ]).map(col => {
                    const isSel = (selectedColor || activeCar.colors?.[0]?.name || "").toLowerCase() === col.name.toLowerCase();
                    return (
                      <div
                        key={col.name}
                        onClick={() => {
                          setSelectedColor(col.name);
                          updateDealState({ vehicle: { ...activeCar, color: col.name } });
                          setActiveQuestionStep(3);
                        }}
                        className={`bg-[#10172c] border rounded-2xl p-4 cursor-pointer transition-all flex items-center justify-between gap-3 hover:scale-[1.02] ${isSel
                          ? 'border-cyan-400 bg-[#16233f] ring-2 ring-cyan-400/20 shadow-lg'
                          : 'border-slate-750 hover:border-cyan-400 hover:bg-[#131b2e]'
                          }`}
                      >
                        <div className="flex items-center gap-3">
                          <span
                            className="w-5 h-5 rounded-full border border-slate-500 shadow-sm flex-shrink-0"
                            style={{ backgroundColor: col.hex || '#333' }}
                          />
                          <span className="text-sm font-bold text-white">{col.name}</span>
                        </div>

                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );

      case 3: // Trade-In Question (Yes / No)
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`Do you have a vehicle you are trading in today, ${customerName || 'Friend'}?`}
              subtitle="Trade allowances reduce your taxable amount and are subtracted directly from your total bill."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 animate-fadeInUp">
                <button
                  onClick={() => {
                    setHasTrade(true);
                    setActiveQuestionStep(4);
                  }}
                  className="bg-cyan-500/15 border border-cyan-500/40 hover:border-cyan-400 p-6 rounded-3xl text-center cursor-pointer transition-all hover:scale-105 space-y-2"
                >
                  <div className="w-10 h-10 rounded-2xl bg-cyan-400 text-slate-950 flex items-center justify-center font-black mx-auto">
                    <FaCarSide size={16} />
                  </div>
                  <h4 className="text-base font-black text-cyan-300">Yes — Value My Trade</h4>
                  <p className="text-xs text-slate-300">Get instant AI market trade appraisal.</p>
                </button>

                <button
                  onClick={() => {
                    setHasTrade(false);
                    setTradeAllowanceValuation(0);
                    setTradePayoff(0);
                    updateDealState({ trade: { hasTrade: false, allowance: 0, payoff: 0 } });
                    setActiveQuestionStep(8); // Skips straight to Step 8!
                  }}
                  className="bg-[#12182b] border border-slate-700 hover:border-slate-500 p-6 rounded-3xl text-center cursor-pointer transition-all hover:scale-105 space-y-2"
                >
                  <div className="w-10 h-10 rounded-2xl bg-slate-800 text-slate-300 flex items-center justify-center font-black mx-auto">
                    ✕
                  </div>
                  <h4 className="text-base font-black text-white">No Trade-In</h4>
                  <p className="text-xs text-slate-300">Skip directly to annual mileage habit.</p>
                </button>
              </div>
            )}
          </div>
        );

      case 4: // Trade Vehicle Info (Year, Make, Model, Color) - Styled with Image 3 Input Box
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text="Let's appraise it. What are we looking at?"
              subtitle="Enter your trade vehicle year, make, model and color."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-4 pt-2 animate-fadeInUp">
                <RoundedPillInput
                  value={tradeVehicleInput}
                  onChange={setTradeVehicleInput}
                  placeholder="Year, make and model — e.g. 2019 Honda Civic EX Silver"
                  onSubmit={() => {
                    const parsed = tradeVehicleInput.trim() || "2019 Honda Civic EX";
                    setTradeDetails(prev => ({ ...prev, model: parsed }));
                    setActiveQuestionStep(5);
                  }}
                />
              </div>
            )}
          </div>
        );

      case 5: // Trade Mileage (Options + Custom Input)
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text="How many miles are on it?"
              subtitle="Select your odometer range or enter exact mileage."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-4 pt-2 animate-fadeInUp">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    "Under 30,000 mi",
                    "30,000 – 60,000 mi",
                    "60,000 – 100,000 mi",
                    "Over 100,000 mi"
                  ].map(m => (
                    <button
                      key={m}
                      onClick={() => {
                        setTradeDetails(prev => ({ ...prev, mileageRange: m }));
                        setActiveQuestionStep(6);
                      }}
                      className="p-4 rounded-2xl bg-[#12192e] border border-slate-750 hover:border-cyan-400 text-left cursor-pointer transition-all hover:scale-[1.01] flex items-center justify-between"
                    >
                      <span className="font-bold text-sm text-white">{m}</span>
                      <span className="text-cyan-400 font-bold">&rarr;</span>
                    </button>
                  ))}
                </div>

                <div className="pt-2">
                  <RoundedPillInput
                    value={customTradeMileageInput}
                    onChange={setCustomTradeMileageInput}
                    placeholder="Or enter exact mileage (e.g. 45,200 mi)"
                    onSubmit={() => {
                      if (customTradeMileageInput.trim()) {
                        setTradeDetails(prev => ({ ...prev, mileageRange: customTradeMileageInput }));
                        setActiveQuestionStep(6);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 6: // Trade Condition (Options + Custom Input)
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text="How would you describe its condition?"
              subtitle="Appraisal algorithms adjust trade equity based on mechanical & cosmetic history."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-3 pt-2 animate-fadeInUp">
                {[
                  { title: "Excellent", desc: "No damage, clean history" },
                  { title: "Good", desc: "Normal wear, all services done" },
                  { title: "Fair", desc: "Cosmetic damage or worn tires" },
                  { title: "Rough", desc: "Mechanical or body work needed" }
                ].map(cond => (
                  <div
                    key={cond.title}
                    onClick={() => {
                      setTradeDetails(prev => ({ ...prev, condition: cond.title, conditionText: cond.desc }));
                      setActiveQuestionStep(7);
                    }}
                    className="p-4 rounded-2xl bg-[#12192e] border border-slate-750 hover:border-cyan-400 cursor-pointer transition-all hover:scale-[1.01] flex items-center justify-between"
                  >
                    <div>
                      <h4 className="font-bold text-sm text-white">{cond.title}</h4>
                      <p className="text-xs text-slate-400">{cond.desc}</p>
                    </div>
                    <span className="text-cyan-400 font-bold text-xs">&rarr;</span>
                  </div>
                ))}

                <div className="pt-2">
                  <RoundedPillInput
                    value={customTradeConditionInput}
                    onChange={setCustomTradeConditionInput}
                    placeholder="Describe it in my words..."
                    onSubmit={() => {
                      if (customTradeConditionInput.trim()) {
                        setTradeDetails(prev => ({ ...prev, condition: "Custom", conditionText: customTradeConditionInput }));
                        setActiveQuestionStep(7);
                      }
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 7: // Trade Payoff to Lienholder + AI Market Valuation & Confirmation Screen
        return (
          <div className="space-y-6 max-w-2xl">
            {aiValuationLoading ? (
              <div className="bg-[#10172c] border border-cyan-500/30 rounded-3xl p-8 text-center space-y-4 shadow-2xl animate-pulse">
                <div className="w-16 h-16 rounded-full bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center text-cyan-400 mx-auto animate-spin">
                  <FaExchangeAlt size={24} />
                </div>
                <div className="space-y-1">
                  <h3 className="text-base font-black text-white">Appraising Your Trade-In Live...</h3>
                  <p className="text-xs text-slate-400 max-w-md mx-auto">
                    Analyzing real-time market comps, odometer depreciation, and condition multiplier for your vehicle.
                  </p>
                </div>
              </div>
            ) : aiValuationResult ? (
              <div className="space-y-5 animate-fadeInUp">
                <TypewriterQuestion
                  tagText="AI MARKET APPRAISAL COMPLETE"
                  text={`Here is the market valuation for your ${aiValuationResult.vehicleName || 'vehicle'}.`}
                  subtitle="Review your market trade allowance and net equity deduction below."
                />

                {/* Valuation Metrics Card */}
                <div className="bg-gradient-to-br from-[#121b33] via-[#0d1426] to-[#0a0e1c] border border-cyan-500/40 rounded-3xl p-6 shadow-[0_0_30px_rgba(6,182,212,0.15)] space-y-4">
                  <div className="flex items-center justify-between border-b border-slate-750 pb-3">
                    <div>
                      <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest block">APPRAISED VEHICLE</span>
                      <h4 className="text-sm font-black text-white">{aiValuationResult.vehicleName}</h4>
                    </div>
                    <div className="text-right">
                      <span className="text-[10px] text-slate-400 font-bold block">{aiValuationResult.condition} Condition</span>
                      <span className="text-[10px] text-slate-400 font-bold block">{aiValuationResult.mileage}</span>
                    </div>
                  </div>

                  {/* 3 Metric Pillars */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="bg-[#090e1c] border border-slate-800 p-3.5 rounded-2xl space-y-1">
                      <span className="text-[9px] font-bold text-slate-400 uppercase">Market Value (Allowance)</span>
                      <div className="text-lg font-black text-white">${aiValuationResult.estimatedValue.toLocaleString()}</div>
                      <span className="text-[9px] text-slate-500 block">Est. range ${aiValuationResult.marketLow.toLocaleString()}–${aiValuationResult.marketHigh.toLocaleString()}</span>
                    </div>

                    <div className="bg-[#090e1c] border border-slate-800 p-3.5 rounded-2xl space-y-1">
                      <span className="text-[9px] font-bold text-slate-400 uppercase">Lienholder Payoff</span>
                      <div className="text-lg font-black text-rose-400">
                        {aiValuationResult.payoff === 0 ? "Paid Off ($0)" : `-$${aiValuationResult.payoff.toLocaleString()}`}
                      </div>
                      <span className="text-[9px] text-slate-500 block">
                        {aiValuationResult.payoff === 0 ? "No loan balance owed" : "Remaining balance we pay off"}
                      </span>
                    </div>

                    <div className={`p-3.5 rounded-2xl space-y-1 border ${aiValuationResult.netEquity >= 0
                      ? 'bg-emerald-950/30 border-emerald-500/40'
                      : 'bg-rose-950/30 border-rose-500/40'
                      }`}>
                      <span className={`text-[9px] font-black uppercase tracking-wider block ${aiValuationResult.netEquity >= 0 ? 'text-emerald-400' : 'text-rose-400'
                        }`}>
                        Net Trade Equity
                      </span>
                      <div className={`text-xl font-black ${aiValuationResult.netEquity >= 0 ? 'text-emerald-300' : 'text-rose-300'
                        }`}>
                        {aiValuationResult.netEquity >= 0
                          ? `+$${aiValuationResult.netEquity.toLocaleString()}`
                          : `-$${Math.abs(aiValuationResult.netEquity).toLocaleString()}`
                        }
                      </div>
                      <span className="text-[9px] text-slate-300 block">
                        {aiValuationResult.netEquity >= 0 ? "Deducted directly from bill" : "Rolled into deal balance"}
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-slate-300 bg-[#080d1a] border border-slate-800 p-3 rounded-xl leading-relaxed">
                    {aiValuationResult.payoff === 0 ? (
                      <span>🎉 Your vehicle is fully paid off! The complete trade allowance of <strong className="text-emerald-400">${aiValuationResult.estimatedValue.toLocaleString()}</strong> will be subtracted directly from your total vehicle price.</span>
                    ) : aiValuationResult.netEquity >= 0 ? (
                      <span>✓ We will settle your lienholder loan balance of <strong className="text-white">${aiValuationResult.payoff.toLocaleString()}</strong> and deduct your net equity of <strong className="text-emerald-400">+${aiValuationResult.netEquity.toLocaleString()}</strong> directly from your total deal amount.</span>
                    ) : (
                      <span>⚠️ Your loan balance of <strong className="text-white">${aiValuationResult.payoff.toLocaleString()}</strong> exceeds current market value. The difference of <strong className="text-rose-400">-${Math.abs(aiValuationResult.netEquity).toLocaleString()}</strong> will be added to your financed amount.</span>
                    )}
                  </p>
                </div>

                {/* Yes / No Decision Actions */}
                <div className="space-y-3 pt-1">
                  <span className="text-xs font-bold text-slate-300 block">Do you want to apply this trade-in to reduce your deal total?</span>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      onClick={() => {
                        setHasTrade(true);
                        const allowance = aiValuationResult.estimatedValue;
                        const payoff = aiValuationResult.payoff;
                        setTradeAllowanceValuation(allowance);
                        setTradePayoff(payoff);
                        updateDealState({
                          tradeIn: {
                            hasTrade: true,
                            vehicle: aiValuationResult.vehicleName,
                            allowance: allowance,
                            payoff: payoff,
                            equity: allowance - payoff
                          }
                        });
                        toast.success(`Applied $${(allowance - payoff).toLocaleString()} Trade Equity!`);
                        setActiveQuestionStep(8);
                      }}
                      className="w-full bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-500 text-slate-950 font-black py-4 px-5 rounded-2xl text-xs uppercase tracking-wider shadow-lg hover:scale-[1.02] transition-all cursor-pointer flex items-center justify-center gap-2"
                    >
                      <FaCheckCircle size={15} /> Yes — Apply Trade Equity &rarr;
                    </button>

                    <button
                      onClick={() => {
                        setHasTrade(false);
                        setTradeAllowanceValuation(0);
                        setTradePayoff(0);
                        updateDealState({
                          tradeIn: {
                            hasTrade: false,
                            allowance: 0,
                            payoff: 0,
                            equity: 0
                          }
                        });
                        setActiveQuestionStep(8);
                      }}
                      className="w-full bg-[#12192e] hover:bg-[#1a233d] border border-slate-700 text-slate-300 hover:text-white font-bold py-4 px-5 rounded-2xl text-xs uppercase tracking-wider transition-all cursor-pointer"
                    >
                      ✕ No — Skip Trade & Continue &rarr;
                    </button>
                  </div>

                  <div className="text-center pt-1">
                    <button
                      onClick={() => setAiValuationResult(null)}
                      className="text-xs font-semibold text-cyan-400 hover:text-cyan-300 underline cursor-pointer"
                    >
                      ↺ Change Payoff Amount
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <TypewriterQuestion
                  tagText={currentDealershipTitle.toUpperCase()}
                  text={`Great, that's everything I needed there. ${customerName || 'Friend'}, what's the payoff to your lienholder?`}
                  subtitle="Select your remaining loan balance or enter the exact amount."
                  onComplete={() => setOptionsVisible(true)}
                />

                {optionsVisible && (
                  <div className="space-y-4 pt-2 animate-fadeInUp">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      {[
                        { label: "It's paid off", val: 0 },
                        { label: "About $10,000 left", val: 10000 },
                        { label: "About $18,000 left", val: 18000 }
                      ].map(p => (
                        <button
                          key={p.label}
                          onClick={() => {
                            setTradePayoff(p.val);
                            setTradeDetails(prev => ({ ...prev, payoff: p.val }));
                            handleCalculateAiTradeValuation(p.val);
                          }}
                          className="p-4 rounded-2xl bg-[#12192e] border border-slate-750 hover:border-cyan-400 text-center cursor-pointer transition-all hover:scale-[1.02] hover:bg-[#162240]"
                        >
                          <span className="font-bold text-sm text-white block">{p.label}</span>
                        </button>
                      ))}
                    </div>

                    <div className="pt-2">
                      <RoundedPillInput
                        value={customTradePayoffInput}
                        onChange={setCustomTradePayoffInput}
                        placeholder="Enter my exact payoff — e.g. $12,500"
                        onSubmit={() => {
                          const num = Number(customTradePayoffInput.replace(/[^0-9]/g, "")) || 0;
                          setTradePayoff(num);
                          setTradeDetails(prev => ({ ...prev, payoff: num }));
                          handleCalculateAiTradeValuation(num);
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 8: // Annual Mileage Habit
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`Commute, road trips, both? Estimate your annual miles on the ${activeCar.year || ''} ${activeCar.make || ''} ${activeCar.model || 'Vehicle'}.`}
              subtitle="Select your driving habit to calculate optimal warranty longevity."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-4 pt-2 animate-fadeInUp">
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {['10k / year', '15k / year', '20k / year', '30k / year'].map(m => (
                    <button
                      key={m}
                      onClick={() => {
                        setAnnualMiles(m);
                        setActiveQuestionStep(9);
                      }}
                      className={`py-4 rounded-2xl font-black text-sm border transition-all cursor-pointer ${annualMiles === m
                        ? 'bg-cyan-400 text-slate-950 border-cyan-400 shadow-lg scale-105'
                        : 'bg-[#131b2e] border-slate-750 text-white hover:border-cyan-500'
                        }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 9: // Payment Type (Finance vs Cash)
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`How would you like to take the ${activeCar.year || ''} ${activeCar.make || ''} ${activeCar.model || 'Vehicle'} home — payments, or paid in full?`}
              subtitle="Financing includes low APR and flexible terms; cash payoff settles 100% of bill with $0 interest."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 animate-fadeInUp">
                <div
                  onClick={() => {
                    setPaymentPreference("FINANCE");
                    updateDealState({ finance: { ...deal?.finance, paymentPreference: "FINANCE" } });
                    setActiveQuestionStep(10);
                  }}
                  className={`bg-[#131b2e] border p-6 rounded-3xl cursor-pointer transition-all hover:scale-[1.02] space-y-2 group ${paymentPreference === "FINANCE" ? 'border-cyan-400 bg-cyan-500/10' : 'border-slate-700/80 hover:border-cyan-400'
                    }`}
                >
                  <div className="w-10 h-10 rounded-2xl bg-cyan-500/15 text-cyan-400 flex items-center justify-center font-black text-base group-hover:bg-cyan-400 group-hover:text-slate-950 transition-colors">
                    <FaCoins />
                  </div>
                  <h3 className="text-lg font-black text-white">Finance it</h3>
                  <p className="text-xs text-slate-300">Spread payment with low APR and flexible terms.</p>
                </div>

                <div
                  onClick={() => {
                    setPaymentPreference("CASH");
                    updateDealState({ finance: { ...deal?.finance, paymentPreference: "CASH" } });
                    setLeftViewMode("dealStudio"); // Goes directly to summary!
                  }}
                  className={`bg-[#131b2e] border p-6 rounded-3xl cursor-pointer transition-all hover:scale-[1.02] space-y-2 group ${paymentPreference === "CASH" ? 'border-emerald-400 bg-emerald-500/10' : 'border-slate-700/80 hover:border-emerald-400'
                    }`}
                >
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/15 text-emerald-400 flex items-center justify-center font-black text-base group-hover:bg-emerald-400 group-hover:text-slate-950 transition-colors">
                    <FaCheck />
                  </div>
                  <h3 className="text-lg font-black text-white">Paying cash</h3>
                  <p className="text-xs text-slate-300">One-time single payment in full ($0 finance charges).</p>
                </div>
              </div>
            )}
          </div>
        );

      case 10: // Down Payment Slider + Quick Chips (Auto-advance on click)
        return (
          <div className="space-y-6 max-w-xl">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`How much down payment would you like to put down, ${customerName || 'Friend'}?`}
              subtitle="Click any quick preset to advance, or slide and click the arrow button."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-6 pt-2 animate-fadeInUp bg-[#131b2e] border border-slate-700/80 p-6 rounded-3xl">
                <div className="flex justify-between items-baseline border-b border-slate-750 pb-4">
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-wider">Selected Down Payment:</span>
                  <span className="text-3xl font-black text-cyan-400">${Number(customDownPayment).toLocaleString()}</span>
                </div>

                <div className="space-y-3 pt-2">
                  <input
                    type="range"
                    min="500"
                    max="30000"
                    step="250"
                    value={customDownPayment || 2500}
                    onChange={(e) => {
                      const val = Number(e.target.value);
                      setCustomDownPayment(val);
                      updateDealState({ finance: { ...deal?.finance, downPayment: val } });
                    }}
                    className="w-full h-3 bg-slate-800 rounded-lg appearance-none cursor-pointer accent-cyan-400"
                  />
                  <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-wider">
                    <span>$500 Min</span>
                    <span>$10,000</span>
                    <span>$20,000</span>
                    <span>$30,000 Max</span>
                  </div>
                </div>

                {/* Quick Presets (Clicking these immediately sets & proceeds!) */}
                <div className="grid grid-cols-4 gap-2 pt-2">
                  {[1000, 2500, 5000, 10000].map(amt => (
                    <button
                      key={amt}
                      onClick={() => {
                        setCustomDownPayment(amt);
                        updateDealState({ finance: { ...deal?.finance, downPayment: amt } });
                        setActiveQuestionStep(11);
                      }}
                      className={`py-2.5 rounded-xl text-xs font-bold border transition-all cursor-pointer ${customDownPayment === amt
                        ? 'bg-cyan-400 text-slate-950 border-cyan-400'
                        : 'bg-[#090d18] border-slate-700 text-slate-300 hover:text-white hover:border-cyan-400'
                        }`}
                    >
                      ${amt.toLocaleString()}
                    </button>
                  ))}
                </div>

                <div className="pt-2">
                  <button
                    onClick={() => {
                      if (!customDownPayment) setCustomDownPayment(2500);
                      setActiveQuestionStep(11);
                    }}
                    className="w-full bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 font-black py-4 rounded-2xl text-xs uppercase tracking-wider cursor-pointer shadow-lg hover:scale-[1.01]"
                  >
                    Confirm Down Payment & Continue &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 11: // Lender Loan Plans (11th Step with 1 Main Hero Plan + See More Loan Options Toggle)
        return (
          <div className="space-y-6 w-full">
            <TypewriterQuestion
              tagText={currentDealershipTitle.toUpperCase()}
              text={`Select your financing lender and loan plan, ${customerName || 'Friend'}.`}
              subtitle="AIMEE has pre-approved and ranked the top loan plans for your vehicle and credit profile."
              onComplete={() => setOptionsVisible(true)}
            />

            {optionsVisible && (
              <div className="space-y-6 max-w-3xl pt-2 animate-fadeInUp">

                {/* 🌟 1 PRIMARY AI-RECOMMENDED HERO LOAN PLAN */}
                {top3RecommendedPlans[0] && (() => {
                  const { plan, badge, badgeColor, reason } = top3RecommendedPlans[0];
                  const planId = plan.plan_id || plan._id || plan.planId;
                  const isPlanSelected = selectedPlanIds.includes(planId);
                  const term = plan.term || plan.termMonths || plan.term_months || 60;
                  const estPmt = calcPmt(totalAmountFinanced, plan.apr, term);
                  const lender = lendersList.find(l => (l._id === plan.lender_id || l.lender_id === plan.lender_id || l.name === plan.lenderName)) || lendersList[0];

                  return (
                    <div className="bg-gradient-to-br from-[#131f3b] via-[#0f172e] to-[#0c1220] border-2 border-cyan-400 rounded-3xl p-6 shadow-[0_0_35px_rgba(6,182,212,0.22)] space-y-4">
                      <div className="flex items-center justify-between border-b border-cyan-500/20 pb-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-8 h-8 rounded-xl bg-cyan-400 text-slate-950 flex items-center justify-center font-black text-sm">
                            <FaAward size={16} />
                          </div>
                          <div>
                            <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest block">FEATURED RECOMMENDED PLAN</span>
                            <h4 className="font-extrabold text-sm text-white">Top Match for your {activeCar.model || "Vehicle"}</h4>
                          </div>
                        </div>
                        <span className={`text-[9px] font-black uppercase px-2.5 py-1 rounded-md inline-block bg-gradient-to-r ${badgeColor}`}>
                          {badge}
                        </span>
                      </div>

                      <div
                        onClick={() => handlePlanToggle(plan, lender)}
                        className={`p-5 rounded-2xl border cursor-pointer transition-all flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 ${isPlanSelected
                          ? 'bg-gradient-to-r from-cyan-950/70 to-blue-950/70 border-cyan-400 ring-2 ring-cyan-400/40 shadow-lg'
                          : 'bg-[#0a0f1e] border-slate-700 hover:border-cyan-500/50'
                          }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center mt-0.5 transition-all ${isPlanSelected ? 'border-cyan-400 bg-cyan-400/20' : 'border-slate-600 bg-slate-800'
                            }`}>
                            {isPlanSelected && <div className="w-3 h-3 rounded-full bg-cyan-400" />}
                          </div>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <h5 className="font-black text-base text-white">{plan.name}</h5>
                              <span className="text-xs text-slate-400 font-bold">&middot; {plan.lenderName || lender.name}</span>
                            </div>
                            <p className="text-xs text-cyan-300 font-bold">{plan.apr}% APR &middot; {term} months &middot; {plan.credit_requirements || "Prime Tier"}</p>
                            <p className="text-xs text-slate-300 italic leading-snug">{reason}</p>
                          </div>
                        </div>

                        <div className="text-left sm:text-right border-t sm:border-t-0 border-slate-800 pt-2 sm:pt-0 w-full sm:w-auto">
                          <span className="text-[10px] text-slate-400 font-bold uppercase block">Monthly Payment</span>
                          <span className="text-2xl font-black text-cyan-400">${Math.round(estPmt)}<span className="text-xs text-slate-400 font-normal">/mo</span></span>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* 🔘 "SEE MORE LOAN OPTIONS" TOGGLE BUTTON */}
                <div className="text-center pt-1">
                  <button
                    type="button"
                    onClick={() => setShowAllLoanPlans(prev => !prev)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-[#12192e] hover:bg-[#182340] border border-cyan-500/30 hover:border-cyan-400 text-cyan-300 hover:text-white font-black text-xs uppercase tracking-wider transition-all shadow-md cursor-pointer"
                  >
                    <span>{showAllLoanPlans ? "▲ Hide Other Loan Plans" : `▼ See More Loan Options (${lenderPlansList.length} Total Plans)`}</span>
                  </button>
                </div>

                {/* EXPANDABLE SECTION: OTHER TOP 3 & GROUPED LENDER PLANS */}
                {showAllLoanPlans && (
                  <div className="space-y-6 pt-2 animate-fadeIn">
                    {/* Other Top 3 Recommended Plans */}
                    {top3RecommendedPlans.length > 1 && (
                      <div className="bg-[#0e1426] border border-slate-750 rounded-3xl p-5 space-y-3">
                        <h4 className="text-xs font-black uppercase tracking-wider text-slate-300">Other AI Recommendations</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          {top3RecommendedPlans.slice(1).map(({ plan, badge, badgeColor, reason }) => {
                            const planId = plan.plan_id || plan._id || plan.planId;
                            const isPlanSelected = selectedPlanIds.includes(planId);
                            const term = plan.term || plan.termMonths || plan.term_months || 60;
                            const estPmt = calcPmt(totalAmountFinanced, plan.apr, term);
                            const lender = lendersList.find(l => (l._id === plan.lender_id || l.lender_id === plan.lender_id || l.name === plan.lenderName)) || lendersList[0];

                            return (
                              <div
                                key={planId}
                                onClick={() => handlePlanToggle(plan, lender)}
                                className={`p-4 rounded-2xl border cursor-pointer transition-all flex flex-col justify-between gap-2.5 ${isPlanSelected
                                  ? 'bg-gradient-to-b from-cyan-950/60 to-blue-950/60 border-cyan-400 shadow-[0_0_20px_rgba(34,211,238,0.25)] ring-2 ring-cyan-400/40 scale-[1.02]'
                                  : 'bg-[#0a0f1e] border-slate-750 hover:border-cyan-500/50'
                                  }`}
                              >
                                <div className="space-y-1.5">
                                  <div className="flex justify-between items-center">
                                    <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-md inline-block bg-gradient-to-r ${badgeColor}`}>
                                      {badge}
                                    </span>
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center transition-all ${isPlanSelected ? 'border-cyan-400 bg-cyan-400/20' : 'border-slate-600 bg-slate-800'
                                      }`}>
                                      {isPlanSelected && <div className="w-2 h-2 rounded-full bg-cyan-400" />}
                                    </div>
                                  </div>
                                  <h5 className="font-extrabold text-xs text-white leading-tight">{plan.name}</h5>
                                  <span className="text-[10px] text-cyan-300 font-bold block">{plan.apr}% APR &middot; {term} mos</span>
                                  <p className="text-[9px] text-slate-300 italic leading-snug">{reason}</p>
                                </div>
                                <div className="flex justify-between items-baseline pt-2 border-t border-slate-800">
                                  <span className="text-[9px] text-slate-400 font-bold">{plan.lenderName || "Partner"}</span>
                                  <span className="text-xs font-black text-white">${Math.round(estPmt)}/mo</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* All Partner Lenders */}
                    {lendersList.map((lender) => {
                      const lenderId = lender._id || lender.lender_id || lender.lenderId;
                      const plans = getPlansForLender(lender);
                      if (plans.length === 0) return null;

                      const isLenderActive = selectedLenderId === lenderId;

                      return (
                        <div
                          key={lenderId || lender.name}
                          className={`rounded-3xl border transition-all p-5 space-y-3 ${isLenderActive
                            ? 'bg-[#12192e] border-cyan-500/40 shadow-[0_0_25px_rgba(6,182,212,0.12)]'
                            : 'bg-[#0e1426] border-slate-800/80 opacity-75 hover:opacity-100 hover:border-slate-700'
                            }`}
                        >
                          <div className="flex justify-between items-center border-b border-slate-750 pb-2.5">
                            <div className="flex items-center gap-2.5">
                              <span className={`w-3 h-3 rounded-full flex items-center justify-center ${isLenderActive ? 'bg-cyan-400 animate-pulse' : 'bg-slate-600'
                                }`} />
                              <FaBuilding className={isLenderActive ? "text-cyan-400" : "text-slate-400"} size={14} />
                              <h4 className="font-extrabold text-sm text-white tracking-wide">{lender.name}</h4>
                            </div>
                            <span className={`text-[10px] font-bold uppercase px-2.5 py-0.5 rounded-lg border ${isLenderActive
                              ? 'text-cyan-300 bg-cyan-500/15 border-cyan-500/30'
                              : 'text-slate-400 bg-slate-800 border-slate-700'
                              }`}>
                              {lender.type || "Partner Lender"}
                            </span>
                          </div>

                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                            {plans.map(plan => {
                              const planId = plan.plan_id || plan._id || plan.planId;
                              const isPlanSelected = isLenderActive && selectedPlanIds.includes(planId);
                              const term = plan.term || plan.termMonths || plan.term_months || 60;
                              const estPmt = calcPmt(totalAmountFinanced, plan.apr, term);

                              return (
                                <div
                                  key={planId}
                                  onClick={() => handlePlanToggle(plan, lender)}
                                  className={`p-4 rounded-2xl border cursor-pointer transition-all flex items-start justify-between gap-3 ${isPlanSelected
                                    ? 'bg-gradient-to-br from-cyan-950/50 via-[#152038] to-blue-950/50 border-cyan-400 ring-2 ring-cyan-400/30 shadow-[0_0_20px_rgba(34,211,238,0.2)] scale-[1.02]'
                                    : isLenderActive
                                      ? 'bg-[#0b1122] border-slate-700/80 hover:border-cyan-500/50 hover:bg-[#101930]'
                                      : 'bg-[#090e1c] border-slate-800 hover:border-slate-700'
                                    }`}
                                >
                                  <div className="flex items-start gap-3">
                                    <div className={`w-5 h-5 rounded-full border flex items-center justify-center mt-0.5 transition-colors ${isPlanSelected ? 'border-cyan-400 bg-cyan-400/20 shadow-sm' : 'border-slate-600 bg-slate-800'
                                      }`}>
                                      {isPlanSelected && <div className="w-2.5 h-2.5 rounded-full bg-cyan-400" />}
                                    </div>
                                    <div className="space-y-0.5">
                                      <h5 className="font-bold text-xs text-white">{plan.name}</h5>
                                      <p className="text-[10px] text-slate-300 font-medium">{plan.apr}% APR &middot; {term} mos</p>
                                    </div>
                                  </div>
                                  <span className="text-xs font-black text-white whitespace-nowrap pl-2">${Math.round(estPmt)}/mo</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div className="pt-2">
                  <button
                    onClick={() => setActiveQuestionStep(12)}
                    className="w-full bg-gradient-to-r from-cyan-400 to-blue-600 text-slate-950 font-black py-4 rounded-2xl text-xs uppercase tracking-wider cursor-pointer shadow-lg hover:scale-[1.01]"
                  >
                    Confirm Loan Plan & Continue to F&I Protection &rarr;
                  </button>
                </div>
              </div>
            )}
          </div>
        );

      case 12: // F&I Protection & Insurance Plans (12th Step with Inline AIMEE AI F&I Manager UI)
        return (
          <div className="space-y-5 max-w-3xl animate-fadeIn">
            {/* Header matching design */}
            <div className="flex justify-between items-end border-b border-slate-850 pb-3">
              <div>
                <span className="text-[10px] font-black text-cyan-400 uppercase tracking-widest block mb-0.5">PROTECTION MENU</span>
                <h2 className="text-xl md:text-2xl font-black text-white tracking-tight">Choose what we cover for you</h2>
              </div>
              <div className="text-right">
                <span className="text-xs font-black text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 px-3 py-1 rounded-xl inline-block">
                  ${productTotal.toLocaleString()} added
                </span>
              </div>
            </div>

            {/* 🤖 AIMEE AI F&I MANAGER INLINE BANNER (Matches screenshot parity!) */}
            <div className="bg-[#0b1326] border border-cyan-500/30 rounded-3xl p-5 shadow-[0_0_30px_rgba(6,182,212,0.12)] space-y-3">
              <div className="flex items-center gap-3.5">
                {/* Glowing AI Multi-Color Orb Avatar */}
                <div className="w-11 h-11 rounded-full p-[2px] bg-gradient-to-tr from-cyan-400 via-teal-300 via-indigo-400 to-amber-300 shadow-[0_0_15px_rgba(34,211,238,0.4)] flex items-center justify-center flex-shrink-0">
                  <div className="w-full h-full rounded-full bg-[#080d1a] flex items-center justify-center">
                    <div className="w-5 h-5 rounded-full bg-gradient-to-tr from-cyan-400 via-teal-300 to-indigo-400 animate-pulse opacity-90 shadow-inner" />
                  </div>
                </div>
                <div>
                  <h4 className="text-sm font-extrabold text-white tracking-wide">AIMEE</h4>
                  <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest block">AI F&I MANAGER</span>
                </div>
              </div>

              {/* Dynamic Conversational Message from AIMEE */}
              <p className="text-xs text-slate-200 leading-relaxed font-medium pt-1">
                {aimeeFiScript || `All protection plans have been pre-selected for your ${activeCar.year || ''} ${activeCar.make || ''} ${activeCar.model || 'vehicle'} to give you maximum coverage and warranty peace of mind. Customize your bundle below.`}
              </p>
            </div>

            {/* F&I Protection Products List */}
            <div className="space-y-3.5 pt-1">
              {fandiList.map((prod, i) => {
                const prodId = prod.product_id || prod._id || prod.productId;
                const isSelected = selectedFAndIIds.includes(prodId);
                const isExpanded = expandedFandiSlot === prodId;
                const monthlyCost = calcPmt(prod.price, apr, termMonths);
                const isBase = !!prod.isBasePlan;

                return (
                  <div
                    key={prodId}
                    className={`rounded-3xl border transition-all duration-300 overflow-hidden shadow-lg ${isBase
                      ? isSelected
                        ? 'bg-gradient-to-br from-[#121c38] via-[#101b33] to-[#0c1324] border-amber-400/60 ring-1 ring-amber-400/30'
                        : 'bg-[#0a0f1d] border-amber-500/30 opacity-85'
                      : isSelected
                        ? 'bg-gradient-to-br from-[#0e172a] via-[#101b33] to-[#0c1324] border-cyan-500/40 ring-1 ring-cyan-500/30'
                        : 'bg-[#0a0f1d] border-slate-800/90 opacity-85'
                      }`}
                  >
                    {/* Header Row (Click to Expand / Collapse) */}
                    <div
                      className="p-5 flex items-center justify-between cursor-pointer hover:bg-white/[0.02] transition-colors select-none"
                      onClick={() => setExpandedFandiSlot(isExpanded ? null : prodId)}
                    >
                      <div className="flex items-center gap-4">
                        {/* Checkbox Icon / Toggle Button */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleFAndI(prod);
                          }}
                          className={`w-10 h-10 rounded-full flex items-center justify-center font-black text-sm shadow-md transition-all cursor-pointer border-0 ${isSelected
                            ? isBase
                              ? 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-amber-500 text-slate-950 scale-105 ring-2 ring-amber-400/50 hover:opacity-90'
                              : 'bg-gradient-to-tr from-cyan-400 via-teal-300 to-emerald-400 text-slate-950 scale-105 ring-2 ring-cyan-400/30 hover:opacity-90'
                            : 'bg-[#141b2e] border border-slate-700 text-slate-400 hover:border-slate-500 hover:text-white'
                            }`}
                          title={isSelected ? "Click to remove coverage" : "Click to add coverage"}
                        >
                          {isSelected ? '✓' : '+'}
                        </button>

                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="text-sm font-black text-white tracking-wide">
                              Slot {i + 1} &middot; {prod.name}
                            </h4>
                            {isBase && (
                              <span className="px-2 py-0.5 bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-950 text-[9px] font-black rounded shadow-sm flex items-center gap-1">
                                <FaStar size={9} /> BASE PLAN
                              </span>
                            )}
                          </div>
                          <p className="text-[11px] text-slate-400 font-medium mt-0.5">
                            {prod.term || "7 yr / 100,000 mi"} &middot; {prod.provider || prod.category || "Pohanka Certified Care"}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        <span className={`text-sm font-black ${isBase ? 'text-amber-400' : 'text-cyan-400'}`}>
                          {isSelected ? `$${monthlyCost.toFixed(2)}/mo` : `$0/mo`}
                        </span>
                        <span className="text-slate-400 text-xs font-bold w-5 h-5 flex items-center justify-center rounded-lg bg-slate-800/80">
                          {isExpanded ? '▲' : '▼'}
                        </span>
                      </div>
                    </div>

                    {/* Detailed Body Section (Collapsed by Default, Opens on Click) */}
                    {isExpanded && (
                      <div className="px-6 pb-5 pt-2 border-t border-slate-800/80 text-xs space-y-3 animate-fadeIn">
                        <p className="text-slate-300 leading-relaxed font-normal">
                          {prod.description}
                        </p>

                        {/* When SELECTED: Show Benefits / Features */}
                        {isSelected ? (
                          <div className="space-y-2 pt-1 animate-fadeIn">
                            <span className="text-[9px] font-black text-emerald-400 uppercase tracking-wider block">INCLUDED COVERAGE BENEFITS</span>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-slate-200">
                              {(prod.benefits || [
                                "Covers all mechanical & electrical repairs",
                                "$0 deductible at Pohanka, $100 nationwide",
                                "Rental car + roadside + trip interruption",
                                "Transferable, raises resale value"
                              ]).map((b, bi) => (
                                <div key={bi} className="flex items-start gap-2">
                                  <span className="text-emerald-400 font-black text-xs leading-none mt-0.5">✓</span>
                                  <span className="text-[11px] leading-snug">{b}</span>
                                </div>
                              ))}
                            </div>
                            <div className="flex justify-between items-center pt-2 text-[10px] text-slate-400 border-t border-slate-800/60">
                              <span>Retail ${Number(prod.price).toLocaleString()} &middot; factory coverage ends 3 yr / 36,000 mi and you drive {annualMiles || "12k"} mi/yr</span>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFAndI(prod);
                                }}
                                className="text-rose-400 hover:text-rose-300 font-semibold cursor-pointer border-0 bg-transparent"
                              >
                                ✕ {isBase ? "Decline Base Plan" : "Remove Coverage"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* When UNSELECTED: Show Consequences / Warnings */
                          <div className="space-y-2 pt-1 animate-fadeIn bg-rose-950/20 border border-rose-500/20 p-3.5 rounded-2xl">
                            <span className="text-[9px] font-black text-rose-400 uppercase tracking-wider block">⚠️ UNPROTECTED &middot; CONSEQUENCES OF DECLINING</span>
                            <div className="space-y-1 text-rose-200/90 text-[11px]">
                              <p>Out-of-pocket liability for all future mechanical, electrical, and labor repair bills averaging $1,800–$4,500.</p>
                            </div>
                            <div className="pt-1.5 flex justify-end">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleToggleFAndI(prod);
                                }}
                                className="bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 text-[10px] font-bold px-3 py-1.5 rounded-xl cursor-pointer transition-all"
                              >
                                + Add Coverage Back (+${monthlyCost.toFixed(2)}/mo)
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="pt-3">
              <button
                onClick={() => setLeftViewMode("dealStudio")}
                className="w-full bg-gradient-to-r from-cyan-400 via-blue-500 to-indigo-600 text-slate-950 font-black py-4 rounded-2xl text-xs uppercase tracking-wider cursor-pointer shadow-lg hover:scale-[1.01]"
              >
                Confirm Protection Plans & View Deal Studio Summary &rarr;
              </button>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // -------------------------------------------------------------
  // RENDER DEAL BOARD CONTENT (SHARED BETWEEN DESKTOP & MOBILE DRAWER)
  // -------------------------------------------------------------
  const renderDealBoardContent = (isDrawer = false) => {
    return (
      <div className="flex-grow overflow-y-auto scrollbar-thin space-y-4 pb-6">
        {/* Right Sidebar Car Banner with Video & Image Fallback */}
        <div className="h-44 sm:h-48 w-full bg-[#080d1a] overflow-hidden relative shadow-xl">
          {activeCar.videoUrl && !sidebarVideoError ? (
            <video
              src={activeCar.videoUrl}
              autoPlay
              loop
              muted
              playsInline
              onError={() => setSidebarVideoError(true)}
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={getCarImageUrl(activeCar, selectedColor || activeCar.colors?.[0]?.name || activeCar.color)}
              alt="Car"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c1220]/80 via-transparent to-transparent pointer-events-none" />
        </div>

        <div className="space-y-1 mx-5 sm:mx-6">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="text-base sm:text-lg font-black text-white">{activeCar.year} {activeCar.make} {activeCar.model}</h3>
              <p className="text-xs text-slate-300 font-semibold">{activeCar.trim || selectedTrim} &middot; {selectedColor || activeCar.colors?.[0]?.name || activeCar.color || "Exterior"}</p>
            </div>
            {salespersonId && (
              <span className="text-[9px] font-black uppercase text-cyan-300 bg-cyan-500/15 border border-cyan-400/30 px-2 py-0.5 rounded-full whitespace-nowrap">
                Assigned
              </span>
            )}
          </div>
        </div>

        {/* Line-by-Line Calculation Summary (Strictly Real-Time) */}
        <div className="space-y-2 border-t border-slate-800 pt-3 text-xs mx-5 sm:mx-6">
          <span className="text-[9px] font-black text-cyan-400 uppercase tracking-widest block">Pricing Breakdown</span>
          <div className="flex justify-between text-slate-300">
            <span>Sale price</span>
            <span className="font-bold text-white">${salePrice.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-slate-300">
            <span>Doc fee</span>
            <span className="font-bold text-white">${docFee.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-slate-300">
            <span>MV registration</span>
            <span className="font-bold text-white">${registrationFee.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-slate-300">
            <span>Sales tax ({(taxRate * 100).toFixed(1)}%)</span>
            <span className="font-bold text-white">${salesTax.toLocaleString()}</span>
          </div>
          <div className="flex justify-between text-slate-200 font-extrabold border-t border-slate-750 pt-1">
            <span>Total Amount Due</span>
            <span>${totalAmountDue.toLocaleString()}</span>
          </div>

          {/* REAL-TIME IMMEDIATE TRADE EQUITY DISPLAY */}
          {hasTrade && tradeEquity !== 0 && (
            <div className="flex justify-between text-emerald-400 font-bold border-t border-slate-800/80 pt-1">
              <span>Trade-in Equity</span>
              <span className={tradeEquity >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                {tradeEquity >= 0 ? `-$${tradeEquity.toLocaleString()}` : `+$${Math.abs(tradeEquity).toLocaleString()}`}
              </span>
            </div>
          )}

          {/* Progressive Financing breakdown if Finance chosen */}
          {paymentPreference === "FINANCE" && (
            <>
              {downPayment > 0 && (
                <div className="flex justify-between text-emerald-400 font-semibold">
                  <span>Down payment</span>
                  <span>-${downPayment.toLocaleString()}</span>
                </div>
              )}

              {customerRebate > 0 && (
                <div className="flex justify-between text-emerald-400 font-semibold">
                  <span>Customer rebate ({appliedCouponCode})</span>
                  <span>-${customerRebate.toLocaleString()}</span>
                </div>
              )}

              {/* INDIVIDUAL PROTECTION PLANS BREAKDOWN */}
              {selectedProducts.length > 0 && (
                <div className="space-y-1.5 pt-1.5 border-t border-slate-800/80">
                  <div className="flex justify-between text-[9px] font-black text-cyan-400 uppercase tracking-wider">
                    <span>Dealership Protection Plans ({selectedProducts.length})</span>
                    <span className="font-bold text-cyan-400">+${productTotal.toLocaleString()}</span>
                  </div>
                  {selectedProducts.map((p) => {
                    const pId = p.product_id || p._id || p.productId;
                    const mCost = calcPmt(p.price, apr, termMonths);
                    return (
                      <div key={pId} className="flex justify-between items-center text-[11px] pl-1 text-slate-300 hover:text-white transition-colors">
                        <span className="truncate pr-2 font-medium text-cyan-300">• {p.short_name || p.name}</span>
                        <span className="font-bold whitespace-nowrap flex-shrink-0 text-white">
                          +${Number(p.price).toLocaleString()}
                          {paymentPreference === "FINANCE" && (
                            <span className="text-[9px] text-cyan-400 font-normal ml-1">(+${mCost.toFixed(0)}/mo)</span>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="flex justify-between text-white font-black border-t border-slate-750 pt-1.5 text-xs">
                <span>Total Amount Financed</span>
                <span>${totalAmountFinanced.toLocaleString()}</span>
              </div>
            </>
          )}
        </div>

        {/* Final Payment / Pricing Box - Real-Time Progressive */}
        <div className="bg-[#12192e] border border-cyan-500/30 p-4 rounded-2xl space-y-1 mt-3 shadow-lg mx-5 sm:mx-6">
          {!paymentPreference ? (
            <>
              <span className="text-[9px] font-black text-cyan-400 uppercase tracking-wider block">
                {hasTrade && tradeEquity !== 0 ? "Net Vehicle Total (Trade Applied)" : "Total Vehicle Price"}
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl sm:text-3xl font-black text-white">
                  ${Math.max(0, totalAmountDue - (hasTrade ? tradeEquity : 0)).toLocaleString()}
                </span>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {hasTrade && tradeEquity !== 0
                  ? `Includes $${tradeEquity.toLocaleString()} trade equity reduction`
                  : "Payment mode and finance terms configured in upcoming steps"}
              </p>
            </>
          ) : paymentPreference === "CASH" ? (
            <>
              <span className="text-[9px] font-black text-emerald-400 uppercase tracking-wider block">
                Total Cash Payoff Amount
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl sm:text-3xl font-black text-white">
                  ${(totalAmountDue - (hasTrade ? tradeEquity : 0) - customerRebate).toLocaleString()}
                </span>
                <span className="text-xs font-bold text-slate-300">
                  paid in full ($0/mo)
                </span>
              </div>
              <p className="text-[10px] text-slate-300 mt-1">
                One-time payment in full · No monthly finance charges
              </p>
            </>
          ) : (
            <>
              <span className="text-[9px] font-black text-cyan-400 uppercase tracking-wider block">
                Estimated Monthly Payment
              </span>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl sm:text-3xl font-black text-cyan-400">
                  ${Math.round(monthlyFinancePayment)}
                </span>
                <span className="text-xs font-bold text-slate-300">
                  /mo ({termMonths} mo · {apr}%)
                </span>
              </div>
              <p className="text-[10px] text-slate-300 mt-1">
                Lender: {activePlan.lenderName || activePlan.name} · Base: ${Math.round(baseMonthlyPayment)}/mo {productTotal > 0 ? `+ Protection: +$${Math.round(productMonthlyTotal)}/mo` : ''}
              </p>
            </>
          )}
        </div>

        {isDrawer && (
          <div className="px-5 sm:px-6 pt-2">
            <button
              onClick={() => setMobileDealBoardOpen(false)}
              className="w-full bg-slate-800 hover:bg-slate-700 text-white font-bold py-3 rounded-xl text-xs uppercase"
            >
              Continue Questionnaire &rarr;
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-screen w-full bg-[#080c18] text-slate-200 font-sans flex flex-col lg:flex-row overflow-hidden select-none">
      {renderBasePlanWarningModal()}
      {renderFAndIPlanModal()}
      {renderDraftWarningModal()}

      {/* TABLET / MOBILE STICKY LIVE SUMMARY BAR (< lg) */}
      <div className="lg:hidden bg-gradient-to-r from-[#0c1426] via-[#101b33] to-[#0a0f1e] border-b border-cyan-500/30 px-4 py-2.5 flex items-center justify-between gap-3 shadow-md flex-shrink-0 z-30">
        <div className="flex items-center gap-2.5 truncate">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse flex-shrink-0" />
          <div className="truncate">
            <span className="text-[10px] text-slate-400 font-bold block truncate">
              {activeCar.year} {activeCar.make} {activeCar.model}
            </span>
            <span className="text-xs font-black text-cyan-300">
              {paymentPreference === "FINANCE" 
                ? `$${Math.round(monthlyFinancePayment)}/mo (${termMonths} mo)` 
                : `$${Math.max(0, totalAmountDue - (hasTrade ? tradeEquity : 0) - customerRebate).toLocaleString()}`}
            </span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => setMobileDealBoardOpen(true)}
          className="bg-gradient-to-r from-cyan-400 to-blue-500 hover:brightness-110 text-slate-950 font-black text-[10px] uppercase px-3 py-1.5 rounded-xl flex items-center gap-1.5 cursor-pointer shadow-md flex-shrink-0"
        >
          <FaSlidersH size={11} /> <span>Deal Board</span>
        </button>
      </div>

      {/* LEFT COLUMN: Interactive Questionnaire + Bottom Conversational Bar */}
      <div className="flex-1 flex flex-col relative h-full bg-[#080c18] border-r border-slate-800 overflow-hidden">

        {/* Progress Stepper for 12 Steps */}
        <div className="px-4 sm:px-8 py-3.5 border-b border-slate-800 bg-[#0c1222] flex flex-wrap justify-between items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3 truncate">
            <span className="text-xs font-black text-cyan-400 whitespace-nowrap">STEP {activeQuestionStep}/12</span>
            <span className="text-xs text-slate-400 font-bold">&middot;</span>
            <span className="text-xs text-slate-200 font-bold truncate">
              {[
                'Customer Name', 'Exterior Color', 'Trade-In', 'Trade Vehicle', 'Mileage',
                'Condition', 'Payoff', 'Annual Mileage', 'Payment Type', 'Down Payment',
                'Lender Loan Plans', 'F&I Protection'
              ][activeQuestionStep - 1] || 'Deal Studio'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            {salespersonId && (
              <span className="hidden sm:inline-flex text-[9px] font-bold text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 px-2 py-0.5 rounded-full items-center gap-1">
                <FaIdBadge size={9} /> {salespersonId}
              </span>
            )}
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(num => (
                <span
                  key={num}
                  onClick={() => setActiveQuestionStep(num)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${num === activeQuestionStep
                    ? 'w-4 sm:w-5 bg-cyan-400 shadow-sm'
                    : num < activeQuestionStep
                      ? 'w-1.5 bg-emerald-400'
                      : 'w-1.5 bg-slate-750'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Interactive Question Workspace Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 md:p-10 flex flex-col justify-start scrollbar-thin">
          <div className="my-auto py-2 w-full">
            {renderCurrentQuestionContent()}
          </div>
        </div>

        {/* FLOATING AI SPEECH BUBBLE */}
        {aiSpeechResponse && (
          <div className="px-4 sm:px-8 pb-3 z-30 animate-fadeInUp">
            <div className="bg-[#12192e] border border-cyan-500/40 rounded-2xl p-3.5 flex items-start justify-between gap-3 shadow-[0_10px_30px_rgba(0,0,0,0.8)]">
              <div className="flex items-start gap-3">
                <div className="w-7 h-7 rounded-xl bg-cyan-400 text-slate-950 flex items-center justify-center font-black flex-shrink-0 mt-0.5">
                  <FaRobot size={12} />
                </div>
                <div className="space-y-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-black text-cyan-400 uppercase tracking-wider">AIMEE DEAL GUIDE</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  </div>
                  <p className="text-xs text-slate-100 leading-relaxed font-medium">{aiSpeechResponse}</p>
                </div>
              </div>
              <button
                onClick={() => setAiSpeechResponse(null)}
                className="text-slate-400 hover:text-white text-xs cursor-pointer p-1"
              >
                ✕
              </button>
            </div>
          </div>
        )}

        {/* BOTTOM FLOATING TEXTBOX (Salesperson ID / Promo Code / Questions) */}
        <div className="px-4 sm:px-8 pb-4 sm:pb-6 pt-2 bg-[#080c18] border-t border-slate-800">
          <form onSubmit={handleConversationalSubmit} className="flex items-center gap-2 sm:gap-3">
            <div className="relative flex-1">
              <input
                type="text"
                value={conversationalInput}
                onChange={(e) => setConversationalInput(e.target.value)}
                placeholder="Type answer, assign rep ('SP-104'), or promo ('BONUS1000')..."
                className="w-full bg-[#11182c] border border-slate-700/80 hover:border-slate-600 focus:border-cyan-400 rounded-2xl py-3 pl-4 pr-10 text-xs text-white outline-none transition-all placeholder-slate-400 shadow-inner"
              />
            </div>
            <button
              type="submit"
              className="bg-[#16223d] hover:bg-cyan-400 text-cyan-400 hover:text-slate-950 border border-slate-700 hover:border-cyan-400 w-11 h-11 rounded-2xl flex items-center justify-center transition-all cursor-pointer shadow-lg hover:scale-105 active:scale-95 flex-shrink-0"
            >
              <FaPaperPlane size={12} />
            </button>
          </form>
        </div>
      </div>

      {/* RIGHT COLUMN: PERSISTENT REAL-TIME DEAL BOARD (DESKTOP ONLY lg+) */}
      <div className="w-[420px] xl:w-[440px] bg-[#0c1220] text-white hidden lg:flex flex-col h-full flex-shrink-0 shadow-2xl relative z-10 border-l border-slate-800">
        {renderDealBoardContent(false)}
      </div>

      {/* MOBILE / TABLET DEAL BOARD SLIDE-UP DRAWER */}
      {mobileDealBoardOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex flex-col justify-end lg:hidden animate-fadeIn">
          <div className="bg-[#0c1220] border-t-2 border-cyan-400 rounded-t-[32px] max-h-[88vh] h-[88vh] flex flex-col overflow-hidden shadow-2xl animate-slideUp">
            <div className="p-4 bg-[#080d1a] border-b border-slate-800 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-2">
                <FaSlidersH className="text-cyan-400" />
                <h3 className="text-xs font-black text-white uppercase tracking-widest">REAL-TIME DEAL BREAKDOWN</h3>
              </div>
              <button
                onClick={() => setMobileDealBoardOpen(false)}
                className="bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white px-3 py-1 rounded-xl text-xs font-bold cursor-pointer"
              >
                ✕ Close
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {renderDealBoardContent(true)}
            </div>
          </div>
        </div>
      )}

      {/* Official Automotive Buyer's Order / Deal Sheet Modal */}
      <DealPrintReport
        isOpen={showPrintReportModal}
        onClose={() => setShowPrintReportModal(false)}
        deal={deal}
        customerInfo={{ name: customerName }}
        uploadedDocs={uploadedDocs}
        salespersonId={salespersonId}
        dealershipData={dealershipData}
        paymentDetails={{
          selectedProducts: fandiList.filter(p => selectedFAndIIds.includes(p.product_id || p._id || p.productId)),
          productTotal: fandiList
            .filter(p => selectedFAndIIds.includes(p.product_id || p._id || p.productId))
            .reduce((sum, p) => sum + Number(p.price || 0), 0),
          paymentPreference: paymentPreference || deal?.finance?.paymentType || "FINANCE",
          downPayment: customDownPayment || deal?.finance?.downPayment || 0,
          customerRebate: activeRebate || deal?.finance?.rebate || 0,
          tradeEquity: (tradeAllowanceValuation - tradePayoff) || deal?.trade?.equity || 0,
          apr: deal?.lender?.apr || deal?.finance?.apr || 5.99,
          term: selectedTerm || deal?.finance?.term || 60,
          monthlyPayment: deal?.calculation?.payment || deal?.calculation?.totalPayment || deal?.finance?.payment || 0
        }}
      />
    </div>
  );
};

export default KioskView;
